/**
 * <steward-chat> — Main STEWARD chat widget.
 *
 * A self-contained chat widget with Shadow DOM isolation.
 * Orchestrates streaming, message display, and conversation persistence.
 *
 * Usage:
 *   <steward-chat
 *     api-endpoint="https://api.example.com"  <!-- optional, see below -->
 *     theme="auto"
 *     palette="soleum"
 *     position="bottom-right"
 *     widget-title="Ask STEWARD"
 *   ></steward-chat>
 *
 * JS-only properties — set imperatively, never reflected to attributes:
 *   widget.apiKey = "sk-..."     // secret — deliberately kept off the DOM
 *   widget.authToken = "ey..."   // secret — deliberately kept off the DOM
 *   widget.overrides = { signature: '#608386', 'panel-blur': '12px' }
 *                                // any --steward-* token override
 */
import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { defineOnce } from './register.js';
import { themeProperties } from './steward-theme.js';
import { sharedStyles } from './steward-styles.js';
import { segmentBlocks } from './steward-markdown.js';
import { StewardStreamClient } from './steward-stream.js';
import { bakedApiEndpoint } from './baked-endpoint.js';
import type { StreamCallbacks } from './steward-stream.js';
import { resolveChartPlaceholders } from './steward-message.js';
import type { ArtifactData } from './steward-message.js';
import './steward-input.js';
import './steward-thinking.js';
import type { ThinkingStep } from './steward-thinking.js';
import { getTheme } from './steward-themes.js';
import { createFocusTrap } from './a11y.js';
import type { FocusTrap } from './a11y.js';
import { lockBodyScroll, unlockBodyScroll } from './responsive.js';
import {
  normalizeStarters,
  parseStartersAttribute,
  starterGlyph,
  startersShowGlyphs,
  ROW_ARROW,
  type Starter,
  type StarterInput,
} from './steward-starters.js';

/** Constructable Stylesheets are baseline in modern engines but missing in
 *  Safari < 16.4 / iOS < 16.4. Detect once so we can fall back to a <style>
 *  element on engines that lack support, without crashing on the constructor. */
function supportsConstructableStylesheets(): boolean {
  if (typeof CSSStyleSheet === 'undefined') return false;
  if (typeof Document === 'undefined') return false;
  if (!('adoptedStyleSheets' in Document.prototype)) return false;
  return typeof CSSStyleSheet.prototype.replaceSync === 'function';
}

/** Native <dialog> + showModal() escapes ancestor stacking-context / clipping
 *  traps via the browser's top-layer. We still use it for the IMAGE PREVIEW
 *  modal — clicking an image to inspect it should genuinely take over the
 *  viewport. It is NOT used for the flyout panel (the flyout is non-modal,
 *  see `supportsPopover` below). iOS Safari < 15.4 lacks showModal(); on
 *  those engines the image preview falls back to a position:fixed div. */
function supportsNativeDialog(): boolean {
  if (typeof HTMLDialogElement === 'undefined') return false;
  return typeof HTMLDialogElement.prototype.showModal === 'function';
}

/** Popover API (`popover="manual"` + `showPopover()`/`hidePopover()`) provides
 *  top-layer rendering — same escape from ancestor stacking-context /
 *  clipping traps that `<dialog>::showModal()` gives — but WITHOUT the modal
 *  semantics (no backdrop, no inert page, no focus trap, host page stays
 *  scrollable + clickable). That is exactly what we want for `mode="flyout"`:
 *  a Notion-style side panel, not a modal. Manual mode disables light-dismiss
 *  and the implicit ESC binding so we drive show/hide programmatically.
 *  Baseline 2024 — Chrome 114+, Safari 17+, Firefox 125+. Older engines fall
 *  back to a positioned <div>. */
function supportsPopover(): boolean {
  if (typeof HTMLElement === 'undefined') return false;
  return 'showPopover' in HTMLElement.prototype;
}

/** Walk through nested shadow roots to find the true innermost focused
 *  element. `document.activeElement` reports the host when focus is inside
 *  a shadow root; this drills down so callers capture the actual trigger
 *  (e.g. a host design-system button rendered inside its own shadow tree)
 *  rather than its outer wrapper. Mirrors the same helper in `a11y.ts`. */
function _deepActiveElement(): Element | null {
  if (typeof document === 'undefined') return null;
  let active: Element | null = document.activeElement;
  while (active && active.shadowRoot && active.shadowRoot.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/** How far a reveal frame got. `pacing` and `released` keep the loop alive;
 *  `idle` parks it until the next token restarts it; `done` is the only step
 *  that releases a deferred finalize. */
type RevealStep = 'released' | 'pacing' | 'idle' | 'done';

/** Reveal pacing (D2, owner-tuned 2026-09-15 against the side-by-side
 *  preview). The BOUND is what the spec pins; the step is a CEILING the
 *  bound compresses, so past ~25 blocks the gap shrinks below 120ms rather
 *  than the answer running long. */
const REVEAL_BOUND_MS = 3000;
const REVEAL_STEP_MS = 120;

/** The AI disclosure's fixed copy (EU AI Act Art. 50(1)): never host text. */
const AI_DISCLOSURE = 'STEWARD is AI and can make mistakes.';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
  sources?: Array<{
    filename: string;
    title: string;
    headingPath: string;
    /* The score as a fraction of the retrieval's own adaptive ceiling,
       0-1. The raw RRF magnitude it replaced never leaves the platform:
       those numbers are meaningless by construction, bounded by
       weight/(RRF_K+1), so a reader could only ever misread them. */
    relevance: number;
    vectorMatch: boolean;
  }>;
  streaming?: boolean;
  /** Released blocks, in order — the reveal's paint unit. Absent on user,
   *  error and restored messages, which render from `content`. */
  blocks?: string[];
  /** Set when the turn was stopped (the user hit Stop) — via the server cancel
   *  ack, the mid-stream freeze, or the single-round backstop. Whatever partial
   *  answer arrived is kept, and the message chrome reads this to label the turn
   *  "Stopped." Persisted by _saveSession (with `elapsedMs`) so a reloaded
   *  session still shows the caption and the collapsed-timeline duration. */
  cancelled?: boolean;
  /** Per-assistant-message thinking timeline. Accumulates as `thinking`
   *  SSE events arrive during streaming, then locks once streaming
   *  ends. Rendered as a `<steward-thinking>` element above the bubble
   *  when non-empty. User messages and error messages never have this. */
  thinkingSteps?: ThinkingStep[];
  /** Turn total (ms) from the terminal `done`/`cancelled` event — the
   *  collapsed-timeline aggregate's fallback when there is no `responding`
   *  step to read delivery-start from (cancel/timeout). */
  elapsedMs?: number;
  /** Owned-telemetry trace id (32-hex) from the terminal `done` event.
   *  Presence is the feedback gate: only a completed model answer carries
   *  one — a cancelled turn finalizes before `done`, and a turn whose answer
   *  the output guardrail replaced finalizes on it without storing the id —
   *  so the thumbs affordance renders iff it is set. Session-persisted so a
   *  restored answer stays rateable. */
  traceId?: string;
  /** The user's thumb on this answer. Set optimistically on click,
   *  reverted if the POST fails, replaced when the other thumb is chosen
   *  (server keeps one score per trace, latest wins). Session-persisted. */
  feedback?: 'up' | 'down';
  /** The `metadata` event's mark that this text is the model's output — the
   *  source of the rendered `data-ai-generated` and of the exports' marks.
   *  Never read off `role`: server copy (the output guardrail's fallback, a
   *  canned line, the input refusal) renders under the assistant role, and a
   *  client that fails after streaming keeps the model's tokens under the
   *  error role. Never true on text that is not the model's — server copy,
   *  whose `metadata` says so, and every sentence the widget writes itself.
   *  Session-persisted. */
  aiGenerated?: boolean;
}

@defineOnce('steward-chat')
export class StewardChat extends LitElement {
  // ── HTML attributes ──
  /** A URL in the attribute wins, verbatim; anything else (absent OR
   *  empty) means the build's baked default. A build with no baked
   *  default makes that a loud configuration error. There is no
   *  relative-fetch fallback and no inert state — an embedder that
   *  wants an inert widget does not mount one. */
  @property({ type: String, attribute: 'api-endpoint' }) apiEndpoint: string | null = null;
  @property({ type: String, reflect: true }) theme: 'light' | 'dark' | 'auto' = 'auto';
  @property({ type: String, reflect: true }) position:
    | 'bottom-right'
    | 'bottom-left' = 'bottom-right';
  @property({ type: String, attribute: 'widget-title' }) widgetTitle = 'Ask STEWARD';
  @property({ type: String, attribute: 'tagline' }) tagline = 'Answers, not search results.';
  /** Composer prompt, forwarded to the input inside the shadow root — the
   *  only way an embedder can reword it for their own product. */
  @property({ type: String }) placeholder = 'Ask a question...';
  /** Up to three starter questions for the welcome state, each
   *  `{ question, hint?, icon? }`: a JSON array in the attribute, the same
   *  array as the property. What renders is the first three valid entries
   *  (see steward-starters.ts); a value that is not a JSON array renders no
   *  starters and throws nothing. Assign a fresh array to change them — an
   *  array mutated in place is not seen. */
  @property({ attribute: 'starters', converter: { fromAttribute: parseStartersAttribute } })
  starters: readonly StarterInput[] | null = null;
  @property({ type: String, reflect: true }) palette = 'soleum';
  /** Layout mode. `classic` (default) is the FAB + popup-above-launcher.
   *  `flyout` slides a non-modal side panel in from the right via the
   *  Popover API — the host page stays interactive and scrollable, no
   *  backdrop, panel stays open until explicitly closed (FAB click, X
   *  button, or ESC). `embedded` renders the panel inline inside the
   *  host's container; the host calls open() imperatively (no FAB).
   *  Unknown values fall back to `classic`. */
  @property({ type: String, reflect: true })
  mode: 'classic' | 'flyout' | 'embedded' = 'classic';
  /** Suppress the FAB launcher. The host then owns the open/close trigger
   *  via the public `open()` / `close()` / `toggle()` API.
   *
   *  Single invariant: the FAB is rendered iff
   *  `mode !== 'embedded' && !hideLauncher`. Embedded never renders a FAB
   *  (a floating FAB next to an inline panel is incoherent UX), so toggling
   *  this attribute in embedded mode has no visible effect.
   *
   *  This is a Lit Boolean attribute — presence alone determines truth.
   *  ANY value (including `hide-launcher="false"`) is parsed as `true`.
   *  To re-show the launcher, REMOVE the attribute or assign
   *  `widget.hideLauncher = false`. */
  @property({ type: Boolean, attribute: 'hide-launcher', reflect: true })
  hideLauncher = false;

  // ── JS-only properties — set imperatively, never reflected to attributes.
  //    apiKey/authToken are secrets deliberately kept off the DOM; overrides
  //    carries a structured token map with no sensible attribute serialization. ──
  apiKey: string | null = null;
  authToken: string | null = null;
  onMessage: ((detail: Record<string, unknown>) => void) | null = null;
  /** Fine-grained overrides for any `--steward-*` CSS custom property —
   *  colors, typography, glass alpha/blur, layout dimensions, etc. Keys
   *  map to `--steward-<key>` (omit the prefix); fully-prefixed keys pass
   *  through verbatim. See Theming in the README.
   *
   *  A Lit reactive property — assigning a new object (or `null`) re-runs
   *  _applyPalette() via updated(). `attribute: false` keeps it JS-only;
   *  never reflected to the DOM. Note: in-place mutation of the same
   *  object reference does NOT trigger a re-apply — assign a fresh object. */
  /** Which reveal to run. `blocks` is the shipped behaviour; `typewriter`
   *  restores the retired character drip so the two can be compared on real
   *  traffic. Unsurfaced on purpose — declared in config-surface with
   *  `surfaced: false`, offered by no builder, and outside what the
   *  answer-reveal spec describes (D8). */
  @property({ type: String, attribute: 'reveal-mode' })
  revealMode: 'blocks' | 'typewriter' = 'blocks';
  @property({ attribute: false }) overrides: Record<string, string> | null = null;
  /** Optional reference to the host-provided container element.
   *
   *  Informational only — the widget does not currently read this property.
   *  Embedded mode positions itself via flow positioning
   *  (`:host([mode='embedded']) { width: 100%; height: 100% }`). Setting
   *  this property has no observable effect today; it exists so a future
   *  ResizeObserver wiring can use it without breaking the public API.
   *
   *  Not reflected to attribute, not a Lit @property — assigning it does
   *  NOT trigger a re-render. */
  containerEl: HTMLElement | null = null;

  // ── Internal state ──
  @state() private _open = false;
  @state() private _messages: Message[] = [];
  @state() private _isStreaming = false;
  /** The conversation was started by `send()`, which submits without the
   *  visitor acting on the welcome card — from a closed panel, before the
   *  card, and its AI disclosure, is ever painted. The thread then opens
   *  with the same line instead. Never persisted: a resumed conversation was
   *  disclosed at its first interaction, and "New conversation" returns to
   *  the welcome card. */
  @state() private _disclosureAtHead = false;
  /** Set the instant the user clicks Stop and a server-side cancel actually
   *  fires; drives the input's "Stopping…" affordance + the thinking timeline's
   *  "Stopping…" cue while the `cancelled` ack is in flight. Reset at turn start
   *  (display is gated on streaming, so a stale value between turns is inert). */
  @state() private _cancelRequested = false;
  /** Which live-thinking display mode is selected, and whether the picker flyout
   *  is open. Passed to `<steward-thinking>.mode`, which governs the live render:
   *  'full' = growing vertical timeline · 'step' = current step only · 'hidden' =
   *  no live timeline. All modes converge to the same completed trace. */
  @state() private _thinkingMode: 'full' | 'step' | 'hidden' = 'full';
  @state() private _thinkingMenuOpen = false;
  /** The row's ⋯ menu (copy and exports). One row menu is open at a time. */
  @state() private _moreMenuOpen = false;
  @state() private _conversationId: string | null = null;
  @state() private _error: string | null = null;
  @state() private _showScrollBtn = false;
  /** A block was released while the user was reading further up. The return
   *  affordance then says new text ARRIVED rather than only offering to move
   *  — the reveal's growth is otherwise invisible to someone scrolled away.
   *  Cleared on re-attach and at turn end (D7). */
  @state() private _grewWhileAway = false;
  @state() private _expanded = false;
  @state() private _expandedSources = new Set<string>();
  @state() private _darkMode = false;
  @state() private _copyFeedback: 'idle' | 'copied' | 'failed' = 'idle';
  @state() private _previewSrc: string | null = null;
  @state() private _previewAlt = '';
  @state() private _previewExpired = false;
  @state() private _previewDownloadUrl: string | null = null;
  @state() private _artifacts = new Map<string, ArtifactData>();

  // Pending clear for the recomposite pulse (see _pulseRecomposite). Non-
  // reactive: a transient rAF handle, not render state. 0 = none scheduled.
  private _recompositeRaf = 0;

  @query('.messages-container') private _messagesContainer!: HTMLElement;
  @query('steward-input') private _inputEl!: HTMLElement & {
    focusInput(): void;
  };
  @query('div.flyout-panel') private _flyoutPanel?: HTMLElement;
  @query('dialog.img-modal-dialog') private _imgDialog?: HTMLDialogElement;

  // Reveal buffer: the answer as it has ARRIVED, plus how much of it has
  // been REVEALED. One string and an offset, not a queue of chunks. The
  // server's 4-char chunking bears no relation to any boundary the reveal
  // cares about — a word now, a top-level markdown block once segmentation
  // lands — so entries were a shape every reader had to undo before it
  // could do anything useful. settled = slice(0, cursor); pending =
  // slice(cursor); and `msg.content` tracks settled exactly, which is the
  // invariant a mid-stream Stop preserves by truncating rather than
  // clearing. Reset per turn at send start (see _sendMessage).
  private _revealSource = '';
  private _revealCursor = 0;
  /** `done` arrived: the source will not grow, so segmentation is total and
   *  every remaining block's end is provable. NOT an instruction to stop
   *  revealing — the bound is the only stopping rule (D5). */
  private _revealTotal = false;
  private _revealBlocks: string[] = [];
  /** Negative infinity, not zero: zero is a real clock value near page load,
   *  and the first block would then sit out an interval it never owed. */
  private _revealLastReleaseAt = Number.NEGATIVE_INFINITY;
  private _revealActive = false;
  private _revealRafId: number | null = null;
  private _revealMsgId: string | null = null;
  // Characters per animation frame (~540 chars/sec at 60fps). 3x the
  // original 3 (owner, 2026-09-15). The bump is what makes the comparison
  // against block reveal honest rather than rigged: at 3 chars/frame a
  // 1,800-character answer drips for ~10s against block reveal's ~3s
  // bound, so the drip would lose on being slow instead of on its actual
  // defect — partial markdown under the reader's eye. At 9 the two land
  // within about a second of each other and the texture is the variable.
  private _typewriterCharsPerFrame = 9;

  // Set once the first answer token arrives; splits Stop into its two phases.
  // Pre-token (false) the answer hasn't streamed — only the server can cancel
  // the in-flight graph. Once true, the whole guardrail-passed answer is already
  // buffered client-side (the server flushes it near-instantly and moves on) and
  // the visible motion is our own typewriter draining the reveal buffer, so Stop is a
  // local freeze. Reset at send start.
  private _deliveryStarted = false;
  // Set when the user stops the turn locally mid-stream. Makes the in-flight
  // stream callbacks (onToken/onCancelled/onDone) no-ops so a trailing server
  // event still buffered in the transport after abort() cannot re-finalize the
  // stopped turn or fire completed-message analytics. Reset at send start.
  private _turnStopped = false;
  // performance.now() at turn start; stamps the cancelled message's elapsedMs on
  // a local mid-stream stop, where the server `cancelled` ack (the usual source)
  // never arrives. Same wall-clock origin as the turn the user is watching.
  private _turnStartMs = 0;

  /** Characters arrived but not yet revealed. */
  private get _revealPending(): number {
    return this._revealSource.length - this._revealCursor;
  }

  private _onRevealDone: (() => void) | null = null;
  private _client: StewardStreamClient | null = null;
  private _boundKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private _introTimer: number | null = null;
  private _imagePreviewHandler: EventListener | null = null;
  private _userScrolledAway = false;
  // Sentinel + IntersectionObserver auto-scroll plumbing. The sentinel is a
  // 1px-tall element appended as the LAST child of `.messages-container` and
  // managed imperatively (NOT in the lit-html template — template re-renders
  // would detach it from observation). The observer reports whether the user
  // is still pinned to the bottom of the message list during streaming. This
  // replaces the old flag-based approach that raced against the browser's
  // async scroll-event dispatch when Markdown tables snapped in mid-stream.
  private _scrollSentinel: HTMLElement | null = null;
  private _scrollObserver: IntersectionObserver | null = null;
  // Tracks whether the user-intent listeners (wheel/touchmove/keydown) are
  // currently bound on `.messages-container`. We attach them lazily inside
  // `_ensureScrollObserver` once the container exists, and detach in
  // `disconnectedCallback`. Separating user-intent (synchronous, from input
  // events) from geometry (async, from IO) lets a wheel/swipe set
  // `_userScrolledAway = true` in the same frame the user acted, so the
  // typewriter drip's auto-pin can't yank them back before the IO callback
  // catches up.
  private _scrollIntentBound = false;
  private _copyFeedbackTimer: number | null = null;
  private _isLocked = false;
  private _mobileQuery: MediaQueryList | null = null;
  // The system's colour scheme, held for the connected lifetime so an `auto`
  // widget follows a change the OS makes while the panel is mounted.
  private _darkQuery: MediaQueryList | null = null;
  private _focusTrap: FocusTrap | null = null;
  // Pre-open focused element for the flyout (non-modal Popover) close path.
  // The Popover API does NOT auto-restore focus on hide (unlike <dialog>),
  // and the existing close handler only tries `.launcher` — which is gone
  // when `hide-launcher` is set. Capturing the host trigger here lets us
  // restore it explicitly, mirroring what the classic focus trap does for
  // its own savedFocus. Cleared on hide.
  private _flyoutSavedFocus: Element | null = null;
  // Captured in willUpdate() the moment a `mode` change is committed. The
  // TD-15 close-and-reopen branch needs to know which mode the widget was
  // CLOSING from (to release its lifecycle resources correctly), but by the
  // time the queued close fires `this.mode` already holds the NEW mode. Lit's
  // `changed.get('mode')` returns the previous value, so we mirror it here
  // for use across the queued ticks.
  private _previousMode: 'classic' | 'flyout' | 'embedded' = 'classic';
  // Per-instance constructable stylesheet that holds palette CSS custom
  // properties. Writing palette colors via adoptedStyleSheets (instead of
  // inline `style.setProperty`) keeps theming compatible with strict
  // `style-src` CSPs that disallow `'unsafe-inline'`. On engines without
  // Constructable Stylesheet support (iOS Safari < 16.4) we fall back to
  // a per-instance <style> element appended to the shadow root.
  private _paletteSheet: CSSStyleSheet | null = null;
  private _paletteStyleEl: HTMLStyleElement | null = null;

  // ── Lifecycle ──

  connectedCallback(): void {
    super.connectedCallback();
    this._darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this._darkQuery.addEventListener('change', this._onDarkChange);
    this._darkMode = this.theme === 'dark' ||
      (this.theme === 'auto' && this._darkQuery.matches);
    this._restoreSession();
    // Restore the thinking-display preference BEFORE first render so a
    // returning-in-session user sees their chosen mode immediately.
    this._restoreThinkingMode();
    // No explicit _applyPalette() here: Lit's first render includes every
    // reactive property (palette, overrides, ...) in changedProperties, so the
    // guard in updated() runs _applyPalette() once before first paint. The
    // per-instance palette sheet persists on the shadow root across
    // disconnect/reconnect, and a stale _darkMode is corrected by the
    // changed.has('_darkMode') branch — so connect needs no separate call.
    // The <style> fallback is the exception: a <style> is parsed again from
    // its text when it is reinserted, and its text is the empty rule (the
    // tokens are written into the parsed rule), so a reconnect writes them
    // again.
    if (this._paletteStyleEl) this._applyPalette();
    if (!this._boundKeyHandler) {
      this._boundKeyHandler = this._handleKeydown.bind(this);
      document.addEventListener('keydown', this._boundKeyHandler);
    }
    // Listen for image-preview events from steward-message (composed: true)
    this._imagePreviewHandler = ((e: Event) => {
      const detail = (e as CustomEvent).detail;
      this._previewSrc = detail.src;
      this._previewAlt = detail.alt;
      this._previewDownloadUrl = detail.downloadUrl || null;
      this._previewExpired = false;
      // Focus close button after render for accessibility
      this.updateComplete.then(() => {
        const closeBtn = this.shadowRoot?.querySelector('.img-modal-btn:last-child') as HTMLElement;
        closeBtn?.focus();
      });
    }) as EventListener;
    this.addEventListener('image-preview', this._imagePreviewHandler);
    this._mobileQuery = window.matchMedia('(max-width: 640px)');
    this._mobileQuery.addEventListener('change', this._onMobileChange);
    // Intro: expand (500ms) → throbbing copper halo (persists until hover)
    this.updateComplete.then(() => {
      const wrap = this.shadowRoot?.querySelector('.launcher-wrap');
      const throb = this.shadowRoot?.querySelector('.halo-throb');
      if (wrap) {
        wrap.classList.add('launcher-wrap--intro');
      }
      if (throb) {
        // Start throb after expand finishes (500ms)
        this._introTimer = window.setTimeout(() => {
          throb.classList.add('halo-throb--intro');
          this._introTimer = null;
        }, 500);
      }
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._client?.abort();
    this._flushReveal();
    this._cancelIntro();
    // Closing the menus is what removes their document listeners, and it
    // leaves neither open for a reconnect to render expanded.
    this._closeMoreMenu();
    this._closeThinkingMenu();
    if (this._imagePreviewHandler) {
      this.removeEventListener('image-preview', this._imagePreviewHandler);
      this._imagePreviewHandler = null;
    }
    if (this._copyFeedbackTimer != null) {
      clearTimeout(this._copyFeedbackTimer);
      this._copyFeedbackTimer = null;
    }
    // Cancel any in-flight recomposite pulse so it can't leave the host stuck
    // with the `recompositing` attribute (and its brightness nudge) if teardown
    // lands between the pulse's two frames.
    cancelAnimationFrame(this._recompositeRaf);
    this._recompositeRaf = 0;
    this.removeAttribute('recompositing');
    this._unlockBodyScroll();
    this._focusTrap?.deactivate();
    this._focusTrap = null;
    if (this._mobileQuery) {
      this._mobileQuery.removeEventListener('change', this._onMobileChange);
      this._mobileQuery = null;
    }
    if (this._darkQuery) {
      this._darkQuery.removeEventListener('change', this._onDarkChange);
      this._darkQuery = null;
    }
    if (this._boundKeyHandler) {
      document.removeEventListener('keydown', this._boundKeyHandler);
      this._boundKeyHandler = null;
    }
    // Auto-scroll plumbing teardown. Disconnect the IntersectionObserver,
    // detach the user-intent listeners, and drop the sentinel ref. The
    // sentinel itself is owned by the shadow DOM and goes away with the
    // host element.
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
      this._scrollObserver = null;
    }
    if (this._scrollIntentBound && this._messagesContainer) {
      this._messagesContainer.removeEventListener('wheel', this._onUserScrollIntent);
      this._messagesContainer.removeEventListener('touchmove', this._onUserScrollIntent);
      this._messagesContainer.removeEventListener('keydown', this._onUserScrollIntent);
    }
    this._scrollIntentBound = false;
    this._scrollSentinel = null;
  }

  firstUpdated(): void {
    // Auto-scroll plumbing — sentinel + IntersectionObserver. Set up here so
    // that the observer instance is constructed exactly once over the
    // component's lifetime; the sentinel may be (re-)attached later, when
    // `.messages-container` first appears in the DOM (classic mode mounts the
    // panel only when _open=true). _ensureScrollObserver is idempotent — it
    // exits cleanly if the container isn't there yet, and gets re-driven from
    // updated() once it is.
    this._ensureScrollObserver();
  }

  /** Idempotent: ensures `_scrollObserver` is constructed and the
   *  `_scrollSentinel` is appended as the LAST child of `.messages-container`
   *  and being observed. Safe to call repeatedly — no-ops once both are wired
   *  to the current container; re-attaches the sentinel if a panel re-mount
   *  has detached it. */
  private _ensureScrollObserver(): void {
    const container = this._messagesContainer;
    if (!container) return;

    if (!this._scrollObserver) {
      // Construct the observer once. `root: container` scopes intersection
      // computation to the messages list (NOT the document viewport). The
      // 40px bottom rootMargin matches the historical user-scrolled-away
      // tolerance — when the sentinel is within 40px of leaving the
      // container's bottom edge, it's still considered "intersecting".
      //
      // Separation of duties: the IO is geometry-only and ASYNC. It is the
      // source of truth for "the user has scrolled BACK to the bottom"
      // (intersect → reset _userScrolledAway = false). It deliberately does
      // NOT set _userScrolledAway = true on de-intersect: programmatic
      // scrolls during streaming legitimately move the sentinel out of view
      // for a frame, and the IO callback is async so it can't distinguish
      // user vs auto. User-intent (wheel/touchmove/keydown) is handled
      // synchronously by `_onUserScrollIntent`.
      const callback = (entries: IntersectionObserverEntry[]) => {
        const entry = entries[entries.length - 1];
        if (
          !this._messagesContainer ||
          this._messagesContainer.clientHeight === 0
        ) {
          return;
        }
        if (entry.isIntersecting && this._userScrolledAway) {
          this._userScrolledAway = false;
          this._showScrollBtn = false;
          this._grewWhileAway = false;
        }
      };
      this._scrollObserver = new IntersectionObserver(callback, {
        root: container,
        rootMargin: '0px 0px 40px 0px',
        threshold: 0,
      });
    }

    // Attach user-intent listeners on the container. Wheel/touchmove/keydown
    // fire synchronously when the user attempts to scroll, so we can flip
    // `_userScrolledAway = true` in the same frame and beat any pending
    // auto-pin from the typewriter drip. Bound at most once per container
    // lifetime — the flag is cleared in `disconnectedCallback`.
    if (!this._scrollIntentBound) {
      container.addEventListener('wheel', this._onUserScrollIntent, { passive: true });
      container.addEventListener('touchmove', this._onUserScrollIntent, { passive: true });
      container.addEventListener('keydown', this._onUserScrollIntent);
      this._scrollIntentBound = true;
    }

    // (Re-)create + (re-)attach the sentinel if missing or detached. The
    // sentinel is owned imperatively (NOT in lit-html) so template
    // re-renders never strip it from observation. We append it as the LAST
    // child so it sits at the visual bottom of the message list.
    if (!this._scrollSentinel || !this._scrollSentinel.isConnected) {
      if (this._scrollSentinel) {
        this._scrollObserver.unobserve(this._scrollSentinel);
      }
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.height = '1px';
      sentinel.dataset.stewardScrollSentinel = '';
      this._scrollSentinel = sentinel;
      container.appendChild(sentinel);
      this._scrollObserver.observe(sentinel);
    } else if (this._scrollSentinel.parentElement !== container) {
      // Container changed (e.g. mode swap re-rendered the panel) — move the
      // sentinel into the new container and re-observe.
      this._scrollObserver.unobserve(this._scrollSentinel);
      container.appendChild(this._scrollSentinel);
      this._scrollObserver.observe(this._scrollSentinel);
    }
  }

  willUpdate(changed: Map<string, unknown>): void {
    super.willUpdate(changed);
    // A row menu never outlives the panel: whatever closed it (Close, the
    // host's close(), a mode swap), both menus close with it, so reopening
    // never shows one already expanded with its document listener live.
    if ((changed.has('_open') && !this._open) || changed.has('mode')) {
      this._closeMoreMenu();
      this._closeThinkingMenu();
    }
    // A starters value that is not an array renders nothing, as specified;
    // said once per assignment, since a framework that hands the property
    // the attribute's JSON text lands here silently otherwise.
    if (changed.has('starters') && this.starters != null && !Array.isArray(this.starters)) {
      console.warn(
        '[steward-chat] starters is not an array; no starter questions are shown. Assign an array to the property, or JSON text to the attribute.',
      );
    }
    // Validate mode — unknown values fall back to classic so a typo in the
    // attribute doesn't render a blank widget.
    if (changed.has('mode')) {
      // Capture the previous mode for the TD-15 close-and-reopen branch in
      // updated(): by the time the queued close fires, `this.mode` already
      // holds the NEW mode, so cleanup needs to read the captured prior value
      // to drive the right teardown lifecycle.
      this._previousMode =
        (changed.get('mode') as 'classic' | 'flyout' | 'embedded') ?? 'classic';
      const valid = new Set(['classic', 'flyout', 'embedded']);
      if (!valid.has(this.mode)) {
        console.warn(
          `[STEWARD] unknown mode "${this.mode}"; falling back to "classic".`,
        );
        this.mode = 'classic';
      }
    }
    // A removed `theme` attribute arrives as null, not the default, and an
    // unknown value names no theme either. Only `auto` matches the auto rules
    // and follows the system, so both become it, and the attribute reflects
    // `auto` again. Removing the attribute is an ordinary host act, so this
    // warns about nothing.
    if (changed.has('theme')) {
      const known = new Set(['light', 'dark', 'auto']);
      if (!known.has(this.theme)) {
        this.theme = 'auto';
      }
    }
  }

  updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    // THE THEME AND THE PALETTE COME FIRST: the _open branch below returns
    // early on several paths (an embedded mount among them, since `_open`
    // is in the first update's changes), and anything after it would be
    // skipped on exactly those updates.
    if (changed.has('theme')) {
      this._darkMode = this.theme === 'dark' ||
        (this.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    // Also the sole initial-mount apply — `palette` is always present in the
    // first-render changedProperties, so this runs once before first paint.
    if (
      changed.has('palette') ||
      changed.has('_darkMode') ||
      changed.has('overrides')
    ) {
      this._applyPalette();
      // _applyPalette re-tints the glass surfaces via CSSOM, which WebKit will
      // not re-raster into their cached backdrop-filter layers — the pill keeps
      // its pre-theme composite until an unrelated repaint. Nudge the filter to
      // force the re-raster. Only on a real dark-mode flip (covers the theme
      // button AND an external `theme=` change, both of which route through
      // _darkMode), never the initial mount (old value undefined, no prior
      // composite to refresh).
      if (changed.has('_darkMode') && changed.get('_darkMode') !== undefined) {
        this._pulseRecomposite();
      }
    }
    if (changed.has('_isStreaming')) {
      this.toggleAttribute('streaming', this._isStreaming);
      // When streaming ends, re-evaluate scroll overflow for final content
      if (!this._isStreaming) {
        this._userScrolledAway = false;
        this.updateComplete.then(() => this._checkScrollOverflow());
      }
    }
    if (changed.has('_open')) {
      this.toggleAttribute('open', this._open);
      // Flyout mode uses the Popover API for top-layer rendering WITHOUT
      // modal semantics — host page stays scrollable, clickable, interactive.
      // ESC dismissal is handled by our existing _handleKeydown (popover
      // `manual` value disables the implicit ESC binding). We do NOT install
      // the custom focus trap for flyout: a non-modal panel must let Tab
      // leave (users may want to tab into host UI behind the panel) — that
      // is the whole point of non-modal vs. modal.
      const isFlyoutPopover = this.mode === 'flyout' && supportsPopover();
      const isEmbedded = this.mode === 'embedded';
      if (this._open) {
        // Embedded is non-modal — Tab MUST be allowed to leave the panel and
        // reach host UI. No focus trap, no popover, no body lock side-effects
        // beyond what _lockBodyScroll() already gates on (see _isMobile()).
        // Embedded does NOT render a FAB (see _renderLauncher); the host owns
        // the open/close trigger via the public toggle()/open()/close() API.
        if (isEmbedded) {
          this._lockBodyScroll();
          this.updateComplete.then(() => {
            // Defensive: if a previous flyout-mode session left the popover
            // mounted (TD-15 cross-mode swap), make sure it's not still open
            // when we re-render in embedded.
            if (this._flyoutPanel?.matches(':popover-open')) {
              this._flyoutPanel.hidePopover();
            }
            // No focus trap, no popover — embedded just renders inline.
          });
          this.updateComplete.then(() => this._checkScrollOverflow());
          return;
        }
        this._lockBodyScroll();
        if (isFlyoutPopover) {
          // Capture the pre-open focused element BEFORE showPopover so we
          // can restore it on close. The composer takes focus only once the
          // popover is shown, below, so reading the active element now still
          // sees what held focus as the panel opened: the launcher, a host's
          // own button, or, on a scripted open, wherever focus was. Walk
          // shadow roots so we capture the true innermost focused node when
          // triggers live in nested shadow trees (host design systems).
          this._flyoutSavedFocus = _deepActiveElement();
        }
        // The classic trap's opener, read now for the same reason: the
        // composer takes focus as soon as this render settles, before the
        // trap below activates, so activate() can no longer see what was
        // focused when the panel opened. A local, not a field: the trap
        // outlives one open, and each open hands it its own opener.
        const opener = isFlyoutPopover ? null : _deepActiveElement();
        this.updateComplete.then(() => {
          if (isFlyoutPopover) {
            // Defensive: showPopover() throws InvalidStateError if already
            // open. Check :popover-open before calling.
            const panel = this._flyoutPanel;
            if (panel && !panel.matches(':popover-open')) {
              panel.showPopover();
              // A control inside a popover can take focus only once the
              // popover is shown. _toggle()'s own focus call runs earlier,
              // while the panel is still hidden, so the composer is
              // focused here.
              this._inputEl?.focusInput();
            }
            return;
          }
          // Classic / fallback path — create + activate our focus trap.
          if (!this._focusTrap) {
            this._focusTrap = createFocusTrap(this.shadowRoot!, {
              fallbackFocus: () =>
                this.shadowRoot?.querySelector<HTMLElement>('.launcher') ?? null,
              // _toggle() already focuses the input on open; keep that UX.
              initialFocus: false,
            });
          }
          this._focusTrap.activate(opener);
        });
      } else {
        this._unlockBodyScroll();
        if (isEmbedded) {
          // No close-side cleanup — embedded is non-modal so nothing was set
          // up at open time (no popover, no focus trap). The host owns
          // dismissal of inline panels.
          this.updateComplete.then(() => this._checkScrollOverflow());
          return;
        }
        if (isFlyoutPopover) {
          const panel = this._flyoutPanel;
          // hidePopover() throws if not open — gate on :popover-open.
          if (panel?.matches(':popover-open')) {
            panel.hidePopover();
          }
          // Native <dialog>::showModal saves+restores focus automatically;
          // popover does NOT. Restore focus explicitly to preserve the
          // keyboard-user T-08 invariant. Priority:
          //   1. The element focused immediately before open (host trigger
          //      button if hide-launcher is set, or the FAB otherwise).
          //      `<body>` is no opener: it is what a scripted open from a
          //      page with nothing focused saves, and focusing it would
          //      skip the launcher.
          //   2. The FAB launcher, if still in the DOM and the saved
          //      element is gone (e.g. host re-rendered between open
          //      and close).
          //   3. Drop to body — no other safe target available.
          // Wait for the next render so the launcher's [open] attribute
          // and any open-only style toggles settle before focusing.
          const saved = this._flyoutSavedFocus as HTMLElement | null;
          this._flyoutSavedFocus = null;
          // Only a close restores focus. `_open` starts false, so the first
          // update carries it with no prior value and takes this arm as the
          // widget mounts; restoring then would move the host page's focus
          // to the launcher.
          if (changed.get('_open') === true) {
            this.updateComplete.then(() => {
              if (
                saved &&
                saved !== document.body &&
                typeof saved.focus === 'function' &&
                saved.isConnected
              ) {
                saved.focus();
                return;
              }
              const launcher =
                this.shadowRoot?.querySelector<HTMLElement>('.launcher');
              launcher?.focus();
            });
          }
        } else {
          this._focusTrap?.deactivate();
        }
      }
      // Panel mounted/unmounted — (re-)attach the scroll sentinel +
      // observer to the (possibly new) `.messages-container`.
      // _ensureScrollObserver is idempotent and handles all the cases
      // (first mount, re-mount, closed-container).
      this.updateComplete.then(() => {
        this._checkScrollOverflow();
        this._ensureScrollObserver();
      });
    }
    // TD-15 lift: mode flip while _open=true. Without this branch, e.g.
    // classic→flyout while open leaves the dialog mounted but never
    // showPopover'd; flyout→classic leaves the popover open while the
    // classic panel also tries to render; either→embedded keeps the
    // FAB/popover stack live alongside the inline panel. Close-and-reopen
    // so the new mode's lifecycle (focus trap, popover, FAB suppression,
    // body scroll lock) initializes cleanly.
    //
    // Queue the close-and-reopen via updateComplete so each phase's lifecycle
    // (close branch teardown, then open branch setup) flushes before the next
    // runs. Without queuing, the synchronous close-then-open would race in a
    // single update cycle and leave dangling state from the closing mode.
    //
    // Placed AFTER the _open branch so that, when _open and mode change in
    // the same tick, we don't double-toggle.
    if (changed.has('mode') && this._open && !changed.has('_open')) {
      // Tear down the OUTGOING (previous) mode's resources eagerly, BEFORE
      // queuing the close — once the close runs, `this.mode` already reflects
      // the new mode, so the close branch above would try to clean up the
      // wrong lifecycle. Capturing previousMode in willUpdate() lets us route
      // the right cleanup here.
      const prevWasFlyout =
        this._previousMode === 'flyout' && supportsPopover();
      if (prevWasFlyout && this._flyoutPanel?.matches(':popover-open')) {
        this._flyoutPanel.hidePopover();
      }
      if (this._previousMode === 'classic') {
        this._focusTrap?.deactivate();
      }
      // (embedded prev needs no cleanup — non-modal, nothing was installed.)
      this.updateComplete.then(() => {
        this._open = false;
        // Wait for the close cycle (which fires the appropriate cleanup —
        // hidePopover, focusTrap.deactivate, body-scroll unlock) to flush
        // before reopening in the new mode.
        this.updateComplete.then(() => {
          this._open = true;
          // Settled — host integration tests and route-driven UI can await
          // this. Emit ONLY here (not on every render) so listeners
          // distinguish a cross-mode swap from any other update tick.
          this.updateComplete.then(() => {
            this.dispatchEvent(
              new CustomEvent('steward-mode-settled', {
                detail: { mode: this.mode },
                bubbles: true,
                composed: true,
              }),
            );
          });
        });
      });
    }
    if (changed.has('_previewSrc') && supportsNativeDialog()) {
      // Image preview also goes into top-layer via <dialog>::showModal so it
      // stacks above the flyout dialog when both are open. Without this the
      // preview would render with z-index:10000 BEHIND the flyout's
      // top-layer, leaving the user unable to see what they clicked.
      this.updateComplete.then(() => {
        const dlg = this._imgDialog;
        if (!dlg) return;
        if (this._previewSrc && !dlg.open) {
          dlg.showModal();
        } else if (!this._previewSrc && dlg.open) {
          dlg.close();
        }
      });
    }
  }

  /** Resolve the api-endpoint contract to a fetchable origin, or null
   *  when the widget is unconfigured (no attribute URL AND no baked
   *  default) — loud, because that is a build or embed mistake, never
   *  a state. */
  private _resolveEndpoint(): string | null {
    if (this.apiEndpoint) return this.apiEndpoint;
    const baked = bakedApiEndpoint();
    if (baked) return baked;
    console.error(
      '[steward-chat] api-endpoint is absent and this build carries no baked default - set api-endpoint on the element, or build with VITE_STEWARD_API_ENDPOINT.',
    );
    return null;
  }

  private _getClient(): StewardStreamClient | null {
    const endpoint = this._resolveEndpoint();
    if (endpoint === null) return null;
    // Recreate client every time — apiKey/authToken may change after init
    this._client = new StewardStreamClient(
      endpoint,
      this.apiKey,
      this.authToken,
    );
    return this._client;
  }

  /**
   * Apply palette + user token overrides as CSS custom properties on :host.
   * Priority: this.overrides > palette values > CSS defaults from steward-theme.ts
   *
   * Palette/color writes go through a per-instance constructable
   * `CSSStyleSheet` attached to the shadow root via `adoptedStyleSheets`,
   * NOT through inline `style.setProperty(...)`. Inline style attributes are
   * blocked by strict `style-src` CSPs that omit `'unsafe-inline'`, so
   * hosts running under those policies (banking, healthcare, gov)
   * couldn't theme the widget. The sheet is created once per instance and updated in place
   * with `replaceSync` on every re-apply (palette change, dark-mode toggle,
   * user override change). Lit also manages `adoptedStyleSheets` for its
   * own `static styles`; we append our sheet to whatever Lit has already
   * attached, never replace.
   */
  private _applyPalette(): void {
    const theme = getTheme(this.palette);
    const isDark = this._darkMode;
    const vars = isDark ? { ...theme.light, ...theme.dark } : { ...theme.light };

    // Collect all CSS custom properties for the :host rule.
    const props: Record<string, string> = {};
    for (const [key, value] of Object.entries(vars)) {
      props[`--${key}`] = value;
    }
    props['--steward-svg-grad-left-edge'] = theme.svgGradient.leftEdge;
    props['--steward-svg-grad-left-mid'] = theme.svgGradient.leftMid;
    props['--steward-svg-grad-right-edge'] = theme.svgGradient.rightEdge;
    props['--steward-svg-grad-right-mid'] = theme.svgGradient.rightMid;
    props['--steward-halo-rgb'] = theme.haloRgb;

    // User token overrides take highest priority.
    if (this.overrides) {
      for (const [key, value] of Object.entries(this.overrides)) {
        const prop = key.startsWith('--') ? key : `--steward-${key}`;
        props[prop] = value;
      }
    }

    // Use a mode-aware selector so the palette wins against the static
    // dark-mode rules in steward-theme.ts. Pre-0.5 the palette was applied
    // via inline `style.setProperty()` which has the highest specificity
    // and always won. Post-0.5 (CSP-safe stylesheet) the palette's `:host`
    // (specificity 0,0,1,0) loses to the static `:host([theme="dark"])`
    // (specificity 0,0,2,0). Matching the host-attribute selector restores
    // the prior behavior — palette is the source of truth for theme colors.
    const selector = this._darkMode
      ? ':host([theme="dark"]), :host([theme="auto"])'
      : ':host';
    // THE PALETTE'S INK GLYPH, on its own signature only. A container
    // style query sets the glyph on the composer while the signature still
    // computes to the palette's own value and the glyph token to the
    // theme's untouched #ffffff. A page that brands the fill (on the
    // element, or through overrides) therefore keeps the white glyph it
    // always had, and a page that sets the glyph keeps its own; so does an
    // `overrides` that names the glyph, which drops the rule outright. An
    // engine without style queries keeps white, as before.
    const signature = vars['steward-signature'];
    const glyphOverridden = Object.keys(this.overrides ?? {}).some(
      (key) => (key.startsWith('--') ? key : `--steward-${key}`) === '--steward-on-signature',
    );
    const inkRule =
      theme.signatureInk && signature && !glyphOverridden
        ? ` @container style(--steward-signature: ${signature}) and style(--steward-on-signature: #ffffff) { steward-input { --steward-on-signature: ${theme.signatureInk}; } }`
        : '';
    // THE TOKENS NEVER ENTER THE SHEET AS TEXT. The rule is parsed empty and
    // each token is written into it with setProperty, which takes one name
    // and one value: a value carrying `}` cannot close the rule and open
    // another, so an override cannot inject a rule that beats the static
    // ones (the AI disclosure's literals among them). The engine drops a
    // value it cannot parse instead. This is not a security boundary —
    // whoever sets overrides already owns the page — only the supported
    // API ceasing to be a way to delete the widget's own rules.
    const ruleText = `${selector} {}${inkRule}`;

    // Prefer Constructable Stylesheets (CSP-clean, no inline style). Fall
    // back to a <style> element on engines that don't support them
    // (iOS Safari < 16.4) — still CSP-clean inside a Shadow DOM since
    // shadow stylesheets aren't governed by the host's style-src directive.
    let sheet: CSSStyleSheet | null = null;
    if (supportsConstructableStylesheets()) {
      if (!this._paletteSheet) {
        this._paletteSheet = new CSSStyleSheet();
      }
      this._paletteSheet.replaceSync(ruleText);
      sheet = this._paletteSheet;
      // Append to shadowRoot.adoptedStyleSheets exactly once. Lit's static
      // styles are already in this array — we add to it, never replace it.
      if (this.shadowRoot) {
        const sheets = this.shadowRoot.adoptedStyleSheets;
        if (!sheets.includes(this._paletteSheet)) {
          this.shadowRoot.adoptedStyleSheets = [...sheets, this._paletteSheet];
        }
      }
    } else if (this.shadowRoot) {
      if (!this._paletteStyleEl) {
        this._paletteStyleEl = document.createElement('style');
        this.shadowRoot.appendChild(this._paletteStyleEl);
      }
      this._paletteStyleEl.textContent = ruleText;
      sheet = this._paletteStyleEl.sheet;
    }
    const rule = sheet?.cssRules[0];
    if (rule instanceof CSSStyleRule) {
      for (const [name, value] of Object.entries(props)) {
        rule.style.setProperty(name, value);
      }
    }
  }

  // ── Styles ──

  static styles = [
    themeProperties,
    sharedStyles,
    css`
      :host {
        position: fixed;
        z-index: var(--steward-z-index);
        font-family: var(--steward-font-family);
      }

      /* Position rules anchor the host (FAB launcher) to a viewport corner.
       * Apply to BOTH classic and flyout — in flyout the FAB still sits in
       * the chosen corner AND doubles as a close affordance, and the panel
       * slides in from the FAB's side (see the [position='bottom-left']
       * override on .flyout-panel + the matching slide-in keyframe below).
       * Embedded mode (Phase 2) lives inline inside a host container, so
       * corner anchoring is meaningless there. */
      :host([position='bottom-right']:not([mode='embedded'])) {
        bottom: 20px;
        right: 20px;
      }

      :host([position='bottom-left']:not([mode='embedded'])) {
        bottom: 20px;
        left: 20px;
      }

      /* ── Embedded mode ─────────────────────────────────────────────────
       * Inline rendering inside a host-provided container. The widget's host
       * element (<steward-chat>) becomes a flex/block child of whatever the
       * host wraps it in — the panel fills 100% inline + block size of the
       * widget's own box. We override the :host { position: fixed; z-index }
       * default because embedded must NOT escape its container into the
       * top-layer stacking context. Container queries replace media queries
       * because the widget no longer knows the viewport — sizing reacts to
       * the container. Naming the container "steward" lets nested @container
       * queries scope to this element specifically (avoids matching against
       * an unrelated host container query of the same shape). */
      :host([mode='embedded']) {
        position: static;
        display: block;
        width: 100%;
        height: 100%;
        /* No container-type here: the @container scope belongs on .panel,
         * which is the box the queries measure.
         *
         * This once carried a longer justification about container-type
         * establishing a containing block for fixed descendants and thereby
         * trapping the launcher. Both halves were false — .launcher-wrap is
         * position: relative, and _renderLauncher returns nothing in
         * embedded mode, so there is no launcher here to trap — and
         * container-type applies no layout containment, so it forms no
         * containing block at all. Removed rather than corrected: a reason
         * that does not hold is worse than no reason, and this one kept a
         * real positioning bug hidden in the rule below. */
      }

      /* No outer shadow in embedded mode. Embedded fills a box the host laid
       * out, so a lift can only fall OUTSIDE that box and onto the host's own
       * chrome — there is no page behind a panel that sits flush in a column,
       * a sidebar or a route. The inset top edge stays, painted inside the
       * panel.
       *
       * The selector is deliberately :host(:where(...)) and not
       * :host([mode='embedded']). :where() contributes nothing to
       * specificity, so this weighs (0,1,0) — what a bare :host weighs — and
       * that keeps three things true at once:
       *
       *   - it still beats the unconditional default in themeProperties:
       *     same weight, but that one is earlier in the styles array;
       *   - _applyPalette's sheet still beats IT, in BOTH themes. That sheet
       *     is :host in light (0,1,0) and :host([theme="dark"]) in dark
       *     (0,2,0), and it is adopted after the static styles. Written as
       *     :host([mode='embedded']) this rule weighs (0,2,0) — outweighing
       *     the light sheet and tying the dark one — so widget.overrides
       *     would set this token in dark and be silently ignored in light,
       *     from identical host code;
       *   - an embedder's own stylesheet still beats everything, by
       *     encapsulation context, which sorts before specificity and layers.
       *
       * A cascade layer cannot do this job: unlayered declarations win over
       * layered ones, so layering this rule would hand the panel straight
       * back to the unlayered default in themeProperties. */
      :host(:where([mode='embedded'])) {
        --steward-panel-shadow:
          inset 0 1px 0 rgba(255, 255, 255, var(--steward-glass-edge-light));
      }

      /* The embedded panel-specific overrides (positioning, animation
       * strip) live DOWNSTREAM of the canonical panel rule so the cascade
       * lets the override win. See the embedded override block further
       * down (alongside the position-anchored panel rules). */

      /* ── Launcher wrapper (holds button + halo ring) ── */
      /* Note: in flyout mode the launcher REMAINS visible while open. The
       * non-modal popover panel doesn't cover the bottom-right corner, and
       * the FAB doubles as a close affordance (clicking it toggles closed). */
      .launcher-wrap {
        position: relative;
        width: var(--steward-launcher-size);
        height: var(--steward-launcher-size);
      }

      .launcher-wrap--intro {
        animation: launcherExpand 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }

      @keyframes launcherExpand {
        0%   { transform: scale(0); }
        70%  { transform: scale(1.15); }
        100% { transform: scale(1); }
      }

      /* ── Launcher button (Option A2 — Faithful Liquid Glass + top catchlight) ──
       * The disc is flat clear glass (chip-tier translucent base, no
       * marble curvature) lit by a single top-crown radial catchlight
       * — the way iOS 26 Dock icons read. Three Liquid Glass essentials:
       *   1. saturate() on backdrop — host page colors POP through the
       *      glass instead of muting. THE defining iOS Liquid Glass cue.
       *   2. Heavier blur (28px) — the FAB is bigger than a chip pill;
       *      proper diffusion needs more frost.
       *   3. Top-crown radial highlight — light bends at the curved
       *      glass rim. Single radial (not C's marble pair) so the disc
       *      stays geometrically flat. */
      .launcher {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        background:
          /* Top-crown catchlight — single radial, no bottom rim shadow.
           * The defining iOS Liquid Glass edge-lensing cue. */
          radial-gradient(circle at 50% 18%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 50%),
          /* FAB-specific tint — chip-tier black-on-white in light mode,
           * white-on-dark in dark mode (real frosted glass scatters
           * light, so dark-mode glass should LIGHTEN the host, not
           * darken it). See --steward-launcher-bg in steward-theme.ts. */
          var(--steward-launcher-bg);
        backdrop-filter: blur(28px) saturate(1.7);
        -webkit-backdrop-filter: blur(28px) saturate(1.7);
        /* No CSS border — edge definition comes from the single 1px
         * white outer-rim box-shadow below. A border + box-shadow combo
         * produces a noisy double-edge (dark border + white rim read as
         * separate rings); Apple Liquid Glass uses one cue. */
        border: none;
        /* Focus ring as a box-shadow layer toggled via custom property —
         * zero-impact default; signature glow on :focus-visible.
         * Prepending var(--focus-ring), to every shadow stack means
         * focus combines with hover/streaming without rule-by-rule
         * cartesian product. */
        --focus-ring: 0 0 0 0 transparent;
        box-shadow:
          var(--focus-ring),
          /* Drop shadow — softer than C's bead, more present than flat A */
          0 8px 22px rgba(0, 0, 0, 0.12),
          /* Inset top — thicker than 1px so it complements the radial */
          inset 0 2px 4px rgba(255, 255, 255, var(--steward-glass-edge-light, 0.6));
        /* No inset BOTTOM — its dark-mode alpha (0.25) reads as a dark
         * crescent on the small circular FAB and accidentally creates
         * the marble curvature A2 was meant to avoid. The drop shadow
         * + top radial catchlight + top inset are sufficient depth. */
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        z-index: 1;
      }

      .launcher:hover {
        /* Lift via translateY + brighter inset top. Copper glow lives
         * on the sibling .halo-throb element (intensifies independently
         * via .launcher-wrap:hover) so the launcher's own shadow stack
         * stays focused on shape/lift cues. */
        transform: translateY(-2px);
        box-shadow:
          var(--focus-ring),
          0 12px 26px rgba(0, 0, 0, 0.14),
          inset 0 2px 4px rgba(255, 255, 255, 0.78);
      }

      .launcher:focus-visible {
        /* Focus indicator: soft diffuse copper GLOW, not a hard ring.
         * 12px blur + 2px spread reads as "the FAB lit up" rather than
         * "the FAB has a border", so it doesn't break the clean
         * edge-less aesthetic. Still WCAG 2.4.7-compliant — the glow
         * is detectable above noise on either light or dark hosts. */
        outline: none;
        --focus-ring: 0 0 12px 2px rgba(var(--steward-halo-rgb), 0.45);
      }

      .launcher svg {
        width: 54px;
        height: 54px;
      }

      /* ── Copper glow halo (baseline + hover + streaming + intro) ──
       * Always visible — the disc reads as warm-lit at all times. Sits
       * behind the launcher (z-index:0 vs launcher's z-index:1) so its
       * box-shadow paints around the disc as an ambient aura.
       *
       * The same two values (baseline + intensified) drive every state:
       *   baseline (rest)        → 24px blur, 5px spread, 0.40 alpha
       *   intensified (hover)    → 34px blur, 9px spread, 0.70 alpha
       *   streaming (continuous) → throb between the two
       *   intro (3 iterations)   → throb between the two, then settles
       * No tight ring, no border-style spread — just a soft, large,
       * warm haze that grows and shrinks. */
      .halo-throb {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        pointer-events: none;
        z-index: 0;
        box-shadow: 0 0 24px 5px rgba(var(--steward-halo-rgb), 0.4);
        transition: box-shadow 0.25s ease;
      }

      /* Hover intensification — the wrap is the parent of both .launcher
       * and .halo-throb, so .launcher-wrap:hover catches mouse-over on
       * either child without needing sibling selectors. */
      .launcher-wrap:hover .halo-throb {
        box-shadow: 0 0 34px 9px rgba(var(--steward-halo-rgb), 0.7);
      }

      /* Streaming "thinking" indicator — pulse between baseline and
       * intensified continuously. Replaces the previous spin-the-star
       * animation; warmth grows and recedes like an inhale. */
      :host([streaming]) .halo-throb {
        animation: haloThrob 1.2s ease-in-out infinite;
      }

      /* Intro burst — loops indefinitely until the user first interacts
       * with the FAB (hover, click-to-open, or keyboard focus). The
       * relevant handlers all call _cancelIntro(), which removes this
       * class; the static baseline glow then takes over with a smooth
       * 0.25s box-shadow transition. */
      .halo-throb--intro {
        animation: haloThrob 1.2s ease-in-out infinite;
      }

      @keyframes haloThrob {
        0%   { box-shadow: 0 0 24px 5px rgba(var(--steward-halo-rgb), 0.4); }
        50%  { box-shadow: 0 0 34px 9px rgba(var(--steward-halo-rgb), 0.7); }
        100% { box-shadow: 0 0 24px 5px rgba(var(--steward-halo-rgb), 0.4); }
      }

      /* ── Chat panel ── */
      /* The panel surface is translucent + blurred (frosted glass) so the
       * host page is visible behind. Header and message bubbles inside
       * stay opaque for readability — same pattern as macOS / iOS / Notion
       * sidebar surfaces. Tunable via --steward-panel-bg-alpha and
       * --steward-panel-blur (set alpha to 1 for opaque). */
      /* Glass thickness recipe (shared by .panel and .flyout-panel):
       *   - outer drop shadow (lifted off the page)
       *   - tight outer shadow (anchors the lift)
       *   - inset top highlight (light catching the glass thickness)
       *   - inset bottom shadow (depth on the underside)
       * The inner shadows give a "risen" 3D edge — the surface reads as
       * a discrete object with thickness, not a flat sticker. */
      .panel {
        position: absolute;
        bottom: calc(var(--steward-launcher-size) + 12px);
        width: var(--steward-width);
        height: var(--steward-height);
        background: var(--steward-panel-bg);
        backdrop-filter: blur(var(--steward-panel-blur));
        -webkit-backdrop-filter: blur(var(--steward-panel-blur));
        border: 1px solid color-mix(in srgb, var(--steward-border) 60%, transparent);
        border-radius: var(--steward-border-radius);
        box-shadow: var(--steward-panel-shadow);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: slideUp 0.2s ease-out;
        /* Named container "steward" — descendant @container queries
         * (compact spacing in .messages-container, card-stacked tables
         * in steward-message's shadow) resolve to this panel's box.
         *
         * container-type does NOT make this a containing block for
         * positioned descendants: it applies style and inline-size
         * containment and establishes an independent formatting context,
         * with no layout containment (CSSWG resolved this in 2024). What
         * anchors .scroll-bottom-btn and .sr-only here is position —
         * absolute in classic mode, and relative in embedded, which is set
         * explicitly for that reason.
         *
         * Image modal + launcher are siblings of .panel, not descendants,
         * so position:fixed on either is unaffected. */
        container-type: inline-size;
        container-name: steward;
      }

      /* Classic + expanded: scale to 1.5x in both dimensions, BUT cap
       * height so the panel's top edge never crosses the viewport top
       * minus the flyout margin. On viewports shorter than ~900px (most
       * laptops), the 1.5x height target would push the top edge past
       * (or off) the screen — max-height clips it to whatever fits.
       *
       * Cap math: the panel is anchored at bottom = launcher-size + 12px
       * within :host, and :host sits at bottom: 20px from the viewport.
       * So the panel's bottom is 32 + launcher-size from viewport-bottom.
       * Available vertical room = 100vh - 32 - launcher-size - top-margin.
       *
       * Stays position:absolute within :host's coordinate system (no
       * positioning-context switch) so the resize transitions smoothly. */
      .panel--expanded {
        width: calc(var(--steward-width) * 1.5);
        height: calc(var(--steward-height) * 1.5);
        max-height: calc(100vh - 32px - var(--steward-launcher-size) - var(--steward-flyout-margin));
        max-height: calc(100dvh - 32px - var(--steward-launcher-size) - var(--steward-flyout-margin));
        transition: width 0.2s ease, height 0.2s ease;
      }

      :host([position='bottom-right']) .panel {
        right: 0;
      }
      :host([position='bottom-left']) .panel {
        left: 0;
      }

      /* Embedded panel: skip slideUp (always present, not entering on a
       * launcher click), drop the corner offsets (classic anchors above the
       * FAB; embedded is flow content), and fill the host element's box.
       * The translucent surface + glass tiers from the .panel rule above
       * are inherited untouched, container-type/name among them — this rule
       * restated both on the same element, which changed nothing. */
      :host([mode='embedded']) .panel {
        /* relative, not static, and it is load-bearing. The panel's
         * absolutely-positioned descendants (.scroll-bottom-btn, .sr-only)
         * need this box as their containing block, and in embedded mode it
         * was the only rule in the chain that could be one — :host is static
         * too, and a host laying the panel out in flow has no reason to be
         * positioned either.
         *
         * Until now that job fell to backdrop-filter, which forms a
         * containing block for any non-none value — including the blur(0px)
         * a host sets to flatten the glass. That is fine until the
         * prefers-reduced-transparency: reduce block further down this
         * sheet sets backdrop-filter: none !important — at which point the
         * containing block vanishes and .scroll-bottom-btn resolves against
         * the initial containing block instead, landing 72px above the PAGE
         * bottom, centred on the PAGE. Verified in Chromium — the probe's
         * offsetParent flips from .panel to BODY.
         *
         * container-type does NOT cover this. It applies style and
         * inline-size containment and establishes an independent formatting
         * context, but no LAYOUT containment, so it forms no containing
         * block for positioned descendants (CSSWG resolved this in 2024;
         * css-contain-3 is now an empty placeholder and the behaviour lives
         * in css-conditional-5).
         *
         * relative with no offsets and z-index auto moves nothing and
         * creates no stacking context; the box stays flow content, which is
         * what embedded mode wants. */
        position: relative;
        width: 100%;
        height: 100%;
        bottom: auto;
        right: auto;
        left: auto;
        /* A fade, and nothing else. This used to rise 12px, described as
         * choreography against the host slot's own slide-in — but a host
         * slot that animates open is one embedding pattern among several,
         * and the widget cannot see whether it happened. When it did not,
         * the panel rose inside a box that never moved, and the rise left
         * that box for the first frames: a transformed child contributes to
         * its ancestors' scrollable overflow, so a host whose shell is
         * exactly viewport-height gained a scrollbar for the duration.
         * Opacity alone reads the same inside a slot that does move, and
         * costs nothing in one that does not.
         *
         * The panel element is conditionally rendered on _open, so this
         * fires once per open; close unmounts it, no exit animation. */
        animation: panelEmbeddedReveal 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      }

      @keyframes panelEmbeddedReveal {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Pill-shaped chip group: the window controls' glass. Its buttons are
       * discrete and round, transparent within the pill so the chip surface
       * reads as one continuous glass element. */
      .header-actions {
        display: flex;
        align-items: center;
        gap: 1px;
        padding: 3px;
        background: var(--steward-chip-bg);
        backdrop-filter: blur(var(--steward-chip-blur)) var(--steward-recomposite-nudge, );
        -webkit-backdrop-filter: blur(var(--steward-chip-blur)) var(--steward-recomposite-nudge, );
        border: 1px solid color-mix(in srgb, var(--steward-border) 50%, transparent);
        border-radius: var(--steward-header-group-radius);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, var(--steward-glass-edge-light, 0.6));
      }

      /* When the pill is the sole backdrop-filter layer (empty panel, >=4
       * buttons), WebKit caches its composite and won't re-raster it on a
       * CSSOM-only theme change — the glass keeps the pre-theme tint until an
       * unrelated repaint (hover). _pulseRecomposite() sets this host attribute
       * for one frame on theme toggle; appending brightness(1.001) mutates the
       * filter LIST (not just the sampled backdrop), which is what invalidates
       * the layer. The shift is imperceptible and reverts the next frame.
       *
       * Only .header-actions consumes the nudge. The other glass surfaces
       * (.launcher, .panel, .flyout-panel) share the mechanism but were not
       * observed to go stale: .panel/.flyout-panel always hold child
       * compositing layers (message bubbles) that keep their tree fresh, and
       * .launcher only shows while the panel — and thus the theme control — is
       * closed. Scoping is deliberate; widen the selector if one of them is
       * ever seen holding a stale composite after a theme change. */
      :host([recompositing]) {
        --steward-recomposite-nudge: brightness(1.001);
      }

      .header-btn {
        background: none;
        border: none;
        color: var(--steward-header-text);
        cursor: pointer;
        width: 28px;
        height: 28px;
        padding: 0;
        border-radius: var(--steward-header-btn-radius);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease;
      }

      .header-btn:hover {
        /* currentColor adapts to the per-theme header-text (orange in
         * light, white in dark), so the hover wash always reads as a
         * tint of the icon color rather than a hardcoded white wash. */
        background: color-mix(in srgb, currentColor 18%, transparent);
      }

      .header-btn:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      .header-btn svg {
        width: 16px;
        height: 16px;
      }

      /* ── Layout E: no header bar (owner-ratified 2026-09-28) ──
       * The window's controls float at the panel's top-right; the
       * conversation's sit in a row below the composer. Both are the
       * header's own buttons (.header-btn) and its chip-tier glass
       * (.header-actions), so every --steward-header-* token that styled
       * them in the header styles them where they are now. */

      /* Close, and Expand in classic mode, on the chip glass, drawn like
       * the row's controls: 28px buttons on an unpadded backing, which with
       * the panel's 1px edge and its own puts Close 12px inside the panel's
       * corner. Pinned above the
       * message bubbles: each bubble is its own backdrop-filter stacking
       * context, and without a z-index a later sibling paints over the
       * glass. The thread clears it at rest and dissolves under it when
       * scrolled (see .messages-container). */
      .window-controls {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 3;
        padding: 0;
      }

      /* New and the ⋯ menu on the left once there is a conversation; the
       * Thinking display menu and the theme toggle on the right. */
      .control-row {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 0 var(--steward-spacing) 8px;
        margin-top: calc(6px - var(--steward-spacing));
        flex-shrink: 0;
        position: relative;
        z-index: 2;
      }
      .row-group {
        display: flex;
        align-items: center;
        gap: 2px;
      }
      .row-spacer {
        flex: 1;
      }
      /* New carries its word as well as its glyph, so it is wider than
       * tall, and the round-button token's default 50% would draw an
       * ellipse on it. Capped at half the button's height, 50% draws a
       * capsule instead, while a length a page sets (0, or the console's
       * own radius) comes through unchanged. */
      .control-row .new-btn {
        width: auto;
        gap: 6px;
        padding: 0 10px 0 8px;
        border-radius: min(var(--steward-header-btn-radius), 14px);
        font-family: var(--steward-font-family);
        font-size: 12px;
        font-weight: 500;
      }

      /* ── The row's menus: ⋯ (copy, exports) and Thinking display ──
       * Anchored to their control and opening UPWARD over the composer,
       * since the row is the panel's last line. Solid (--steward-bg-
       * secondary) rather than glass: a backdrop-filter nested inside the
       * panel's samples nothing, so the blur could not frost what is
       * behind. One open at a time; Escape, a choice or a click outside
       * closes. visibility:hidden takes a closed menu out of the a11y tree
       * and the tab order, delayed by the slide so the slide-out plays. */
      .menu-control {
        position: relative;
        display: inline-flex;
        align-items: center;
      }
      .row-menu {
        position: absolute;
        bottom: calc(100% + 6px);
        z-index: 41;
        display: flex;
        flex-direction: column;
        gap: 1px;
        min-width: 180px;
        padding: 4px;
        background: var(--steward-bg-secondary);
        border: 1px solid color-mix(in srgb, var(--steward-border) 50%, transparent);
        border-radius: 10px;
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, var(--steward-glass-edge-light, 0.6)),
          0 -4px 20px rgba(var(--steward-shadow-rgb), 0.14);
        visibility: hidden;
        pointer-events: none;
        transform: translateY(8px);
        transition:
          transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
          visibility 0s linear 0.2s;
      }
      .more-control .row-menu {
        left: 0;
      }
      .thinking-control .row-menu {
        right: 0;
      }
      .menu-control.is-expanded .row-menu {
        visibility: visible;
        pointer-events: auto;
        transform: translateY(0);
        transition:
          transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
          visibility 0s;
      }
      .menu-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        min-height: 34px;
        padding: 0 12px 0 10px;
        background: none;
        border: none;
        border-radius: 6px;
        color: var(--steward-text);
        font-family: var(--steward-font-family);
        font-size: 13px;
        text-align: left;
        white-space: nowrap;
        cursor: pointer;
      }
      .menu-item:hover,
      .menu-item:focus-visible {
        background: color-mix(in srgb, var(--steward-header-text) 12%, transparent);
        outline: none;
      }
      .menu-item svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--steward-header-text);
      }
      .menu-item .menu-check {
        margin-left: auto;
      }
      /* The active mode is not colour alone: a check and a heavier weight,
       * and aria-checked for assistive technology. */
      .menu-item[aria-checked='true'] {
        font-weight: 600;
      }
      .menu-item:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .menu-item--error,
      .menu-item--error svg {
        color: var(--steward-error-text);
      }

      /* ── Messages area ── */
      .messages-container {
        flex: 1;
        overflow-y: auto;
        padding: var(--steward-spacing);
        /* ROOM FOR THE FLOATING WINDOW CONTROLS, which end 40px down inside
         * the panel's edge (10px in, a 28px button, its backing's 1px edge
         * top and bottom): with
         * no header bar the thread starts at the panel's top, so at rest
         * its first line starts a gutter below them rather than under
         * Close. */
        padding-top: calc(var(--steward-spacing) + 40px);
        /* THE TOP EDGE DISSOLVES (ratified 2026-09-29): text scrolling up
         * under the window controls fades into the panel instead of
         * colliding with them. A mask on the scroll box, not an overlay, so
         * it fades the content itself over any ground, glass included, and
         * takes no pointer events — a link under it still takes the click.
         * Opaque exactly where the padding above ends, so at rest nothing
         * is faded.
         *
         * PERMANENT, not switched on by scrolling: a mask makes this box
         * the backdrop root for the bubbles' backdrop-filter, and toggling
         * it made every bubble's glass shift tint as the thread left its
         * top. Always on, the tint is the same at rest and scrolled. */
        -webkit-mask-image: linear-gradient(
          to bottom,
          transparent 0,
          #000 calc(var(--steward-spacing) + 40px)
        );
        mask-image: linear-gradient(to bottom, transparent 0, #000 calc(var(--steward-spacing) + 40px));
        overscroll-behavior: contain;
        /* Disable browser scroll anchoring inside the message list.
         * The streaming pipeline replaces a message's innerHTML on
         * each token chunk via unsafeHTML — the DOM nodes are
         * recreated, not patched in place. Scroll anchoring then
         * picks a new anchor element after each swap, which can yank
         * scrollTop backwards (most visible at streaming-end, when a
         * Markdown table's snap-in DOM gets replaced one last time
         * with the same HTML — anchor migrates, scroll jumps to a
         * pre-table position). The widget owns its own scroll
         * tracking via the IntersectionObserver sentinel + drip-time
         * _scrollToBottom() — we don't need the browser's heuristic. */
        overflow-anchor: none;
      }

      /* Compact layout below the embedded breakpoint. Container queries
       * scope to the widget's own box (named "steward"), so a 400px host
       * slot yields the compact rules regardless of viewport width — the
       * whole point of embedded mode. Placed AFTER the canonical
       * .messages-container rule so source order resolves the cascade in
       * the predicate-true case. The compact rule fires when the named
       * container is <= 480px; otherwise the var(--steward-spacing) rule
       * above continues to apply. */
      @container steward (max-width: 480px) {
        .messages-container {
          padding: var(--steward-spacing-compact);
          padding-top: calc(var(--steward-spacing-compact) + 40px);
          -webkit-mask-image: linear-gradient(
            to bottom,
            transparent 0,
            #000 calc(var(--steward-spacing-compact) + 40px)
          );
          mask-image: linear-gradient(
            to bottom,
            transparent 0,
            #000 calc(var(--steward-spacing-compact) + 40px)
          );
        }
      }

      .message-group {
        margin-bottom: 16px;
      }

      .message-group.has-sources {
        margin-bottom: 8px;
      }

      .welcome {
        text-align: center;
        color: var(--steward-text-secondary);
        padding: 40px 20px;
        font-size: 14px;
      }

      .welcome-icon {
        margin-bottom: 12px;
      }

      .welcome h3 {
        font-size: 16px;
        color: var(--steward-text);
        margin-bottom: 8px;
      }

      /* ── AI disclosure ──
       * The statement that the visitor is talking to an AI (EU AI Act
       * Art. 50(1)), at a conversation's first interaction: the welcome
       * card's last line, from which a visitor types or taps the first
       * message, and the same line opening a thread send() started, which
       * submits without the visitor acting on the welcome card.
       *
       * EVERY PROPERTY THAT DECIDES WHETHER IT CAN BE READ IS A LITERAL.
       * widget.overrides sets any token to any value at the highest
       * priority, so a var() here would hand the disclosure to the host:
       * text-secondary set to transparent erases whatever inherits it.
       *
       * Plain 9px text, quieter than the tagline and grey where the tagline
       * is tinted (owner, 2026-10-02). Each grey is the faintest that keeps
       * 4.5:1 against the panel colour of every built-in palette in its
       * theme. It has no background of its own, so a host that paints the
       * panel this exact grey hides it: a deliberate act, by a page that
       * could as well remove the widget. */
      .ai-disclosure {
        display: block;
        visibility: visible;
        opacity: 1;
        font-size: 9px;
        line-height: 12px;
        color: #6c7374;
        margin-top: 10px;
      }

      :host([theme='dark']) .ai-disclosure {
        color: #7d8485;
      }

      /* The same line opening a thread that send() started: layout only.
       * What decides whether it can be read stays on the shared
       * .ai-disclosure rules. */
      .ai-disclosure--head {
        text-align: center;
        margin-top: 0;
        margin-bottom: 16px;
      }

      @media (prefers-color-scheme: dark) {
        :host([theme='auto']) .ai-disclosure {
          color: #7d8485;
        }
      }

      /* ── Starter questions ──
       * Rows under the welcome card, which keeps its own padding as the
       * space above them. Each is one button: an optional glyph tile, the
       * question over its hint, and an arrow. The edge is the composer's,
       * and the corners step from the inner-corner token like the
       * composer's, so a host that squares one squares both.
       *
       * The rows sit inside the welcome card's own side padding, and never
       * grow past their width in the classic panel: a wider panel (expanded,
       * flyout, a broad embedded slot) centres them rather than stretching
       * a question across the thread. */
      .starters {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding-inline: 20px;
      }
      .starter {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        max-width: calc(var(--steward-width) - 60px);
        padding: 10px 8px 10px 10px;
        background: var(--steward-bg);
        border: 1px solid color-mix(in srgb, var(--steward-border) 60%, var(--steward-text));
        border-radius: calc(var(--steward-border-radius-sm, 8px) + 4px);
        color: var(--steward-text);
        font-family: var(--steward-font-family);
        text-align: left;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .starter:hover {
        background: var(--steward-bg-secondary);
      }
      .starter:focus-visible {
        outline: 2px solid var(--steward-header-text);
        outline-offset: 2px;
      }
      .starter-glyph {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: var(--steward-border-radius-sm, 8px);
        background: var(--steward-bg-secondary);
        color: var(--steward-header-text);
      }
      .starter-glyph svg {
        width: 16px;
        height: 16px;
      }
      .starter-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
      }
      .starter-question {
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
      }
      .starter-hint {
        font-size: 12px;
        line-height: 16px;
        color: var(--steward-text-secondary);
      }
      .starter-arrow {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--steward-bg-secondary);
        color: var(--steward-text-secondary);
      }
      .starter-arrow svg {
        width: 14px;
        height: 14px;
      }

      /* ── Sources ── */
      .sources {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin: 4px 0;
        margin-left: 0;
        max-width: 85%;
      }

      /* Theme-neutral subtle text — derived from --steward-text via
       * color-mix so it stays palette-agnostic across light + dark
       * (mirrors the thinking-timeline label treatment for visual
       * cohesion between meta affordances). */
      .sources-toggle {
        display: block;
        margin: 0 0 8px 0;
        font-size: 12px;
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-family: var(--steward-font-family);
      }

      .sources-toggle:hover {
        color: color-mix(in srgb, var(--steward-text) 80%, transparent);
      }

      /* Thumbs feedback on a completed answer — same theme-neutral
       * color-mix treatment as .sources-toggle so the meta affordances
       * read as one family. The selected thumb is full-strength text
       * color on a subtle wash (state is also carried by aria-pressed,
       * never by color alone). */
      .feedback-row {
        display: flex;
        gap: 4px;
        margin: 4px 0 0 0;
      }

      .feedback-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 4px;
        background: none;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        color: color-mix(in srgb, var(--steward-text) 45%, transparent);
      }

      .feedback-btn svg {
        width: 100%;
        height: 100%;
      }

      .feedback-btn:hover {
        color: color-mix(in srgb, var(--steward-text) 80%, transparent);
        background: color-mix(in srgb, var(--steward-text) 8%, transparent);
      }

      .feedback-btn:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 1px;
      }

      .feedback-btn.selected {
        color: var(--steward-text);
        background: color-mix(in srgb, var(--steward-text) 12%, transparent);
      }

      /* Quiet caption under a stopped answer ("Stopped.") — neutral, derived
       * from --steward-text so palette accents don't leak in. */
      .msg-note {
        margin: 0 0 8px 0;
        font-size: 12px;
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
      }

      /* Theme-neutral pill — color + border both derived from
       * --steward-text via color-mix so palette accents (soleum teal,
       * lux blue, zen stone) don't leak into the meta affordances.
       * Display-only: no hover, no pointer cursor — the title attr
       * carries the full path on focus/hover, but the tag itself
       * isn't an interactive control. */
      .source-tag {
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 12px;
        background: transparent;
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
        border: 1px solid color-mix(in srgb, var(--steward-text) 22%, transparent);
        white-space: nowrap;
      }

      /* ── Scroll-to-bottom button ── */
      /* 20px above the composer, whose top stands 86px above the panel's
       * bottom (the row, its 6px gap and the 44px surface) at any gutter. */
      .scroll-bottom-btn {
        position: absolute;
        bottom: 106px;
        left: 50%;
        transform: translateX(-50%);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid var(--steward-border);
        background: var(--steward-bg);
        color: var(--steward-text-secondary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        transition: opacity 0.2s ease, background 0.15s ease;
      }

      /* Carrying a word, the affordance stops being a round FAB and becomes a
       * pill. Width is content-driven so the label cannot be clipped. */
      .scroll-bottom-btn.has-new {
        width: auto;
        border-radius: 999px;
        gap: 6px;
        padding: 0 12px;
        color: var(--steward-text);
      }

      .scroll-bottom-label {
        font-size: 12px;
        line-height: 1;
        white-space: nowrap;
        z-index: 1;
      }

      .scroll-bottom-btn:hover {
        background: var(--steward-bg-secondary);
        color: var(--steward-text);
      }

      .scroll-bottom-btn svg {
        width: 18px;
        height: 18px;
      }

      /* ── Error banner ── */
      .error-banner {
        padding: 8px 16px;
        background: var(--steward-error-bg);
        color: var(--steward-error-text);
        font-size: 12px;
        text-align: center;
        flex-shrink: 0;
      }

      /* ── Visually hidden (screen-reader only) ── */
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      /* ── Flyout mode: non-modal popover in top-layer ── */
      /* The popover element's top-layer placement escapes ancestor
       * stacking-context and overflow:hidden traps from host CSS — same
       * benefit as <dialog>::showModal — but WITHOUT modal semantics.
       * No backdrop, no inert, no focus trap; the host page stays
       * scrollable and clickable behind the panel. */
      :host([mode='flyout']) .flyout-panel {
        position: fixed;
        /* Float with a uniform margin against top/right/bottom edges of
         * the viewport. Subtle gap (12px default) gives the panel a
         * "lifted" sensation — distinct rounded glass object rather
         * than docked-against-the-edge sheet. left:auto so width
         * remains derived from inset-right + width. */
        top: var(--steward-flyout-margin);
        right: var(--steward-flyout-margin);
        bottom: var(--steward-flyout-margin);
        left: auto;
        width: 50vw;
        max-width: 720px;
        min-width: 360px;
        /* Explicit height — relying on top+bottom to derive height was
         * unreliable when the popover entered the top-layer (some engines
         * compute height as auto-content rather than viewport-minus-insets,
         * collapsing the panel to its content height). dvh tracks the iOS
         * keyboard like before. */
        height: calc(100vh - 2 * var(--steward-flyout-margin));
        height: calc(100dvh - 2 * var(--steward-flyout-margin));
        max-height: calc(100dvh - 2 * var(--steward-flyout-margin));
        padding: 0;
        margin: 0;
        /* Glass thickness — the same token as .panel, so a host that
         * re-elevates one re-elevates both. */
        border: 1px solid color-mix(in srgb, var(--steward-border) 60%, transparent);
        border-radius: var(--steward-border-radius);
        background: var(--steward-panel-bg);
        backdrop-filter: blur(var(--steward-panel-blur));
        -webkit-backdrop-filter: blur(var(--steward-panel-blur));
        box-shadow: var(--steward-panel-shadow);
        color: var(--steward-text);
        overflow: hidden;
        flex-direction: column;
        /* Same container scope as .panel + embedded — keeps the
         * @container queries firing in flyout mode too (matters at
         * narrow viewports where flyout collapses to min-width 360px
         * and benefits from the card-stacked tables / compact spacing). */
        container-type: inline-size;
        container-name: steward;
      }

      /* Hide the popover when it isn't open. The UA stylesheet's
       * [popover]:not(:popover-open) { display: none } rule LOSES to our
       * author rule above when we declare display:flex. Splitting display
       * across the open/closed states fixes that — closed state stays
       * invisible (an empty popover element used to render an empty
       * translucent panel before showPopover() was called). */
      :host([mode='flyout']) .flyout-panel:not(:popover-open) {
        display: none;
      }
      :host([mode='flyout']) .flyout-panel:popover-open {
        display: flex;
      }

      /* ── Flyout panel anchor + slide-in (desktop) ─────────────────────
       * Desktop (>=641px) anchors the panel to the FAB's edge with a 12px
       * gap to the viewport, and slides in from that edge accounting for
       * the margin so the panel enters past the viewport boundary.
       *
       * The bottom-left override and the matching slide-in BOTH live
       * inside the min-width:641px media block because:
       *   1. The base flyout-panel rule is the bottom-right default (it
       *      already sets right:12px and left:auto).
       *   2. The mobile @media (max-width: 640px) block further down
       *      collapses to full-screen via top:0/right:0/bottom:0/left:0 —
       *      a layout that must win for BOTH positions.
       *   3. The bottom-left override selector
       *      :host([mode=flyout][position=bottom-left]) .flyout-panel
       *      has higher specificity than the mobile rule
       *      :host([mode=flyout]) .flyout-panel by one attribute selector.
       *      The min-width gate is the *load-bearing* mechanism that
       *      keeps it from leaking onto mobile and leaving a 12px strip
       *      on the left — NOT specificity, which would favor the
       *      override. A future maintainer removing the gate would
       *      reintroduce the mobile bug.
       *   4. The slide-in keyframes use calc(+/-100% + margin). On mobile
       *      the panel is 100vw, so a +12px overshoot is dead animation
       *      distance — the mobile @media block below assigns separate
       *      margin-free keyframes (flyoutSlideIn{Right,Left}Mobile). */
      @media (min-width: 641px) {
        :host([mode='flyout'][position='bottom-left']) .flyout-panel {
          right: auto;
          left: var(--steward-flyout-margin);
        }

        :host([mode='flyout'][position='bottom-right']) .flyout-panel:popover-open,
        :host([mode='flyout']:not([position='bottom-left'])) .flyout-panel:popover-open {
          animation: flyoutSlideInRight 0.3s ease-out;
        }

        :host([mode='flyout'][position='bottom-left']) .flyout-panel:popover-open {
          animation: flyoutSlideInLeft 0.3s ease-out;
        }
      }

      @keyframes flyoutSlideInRight {
        /* Slide in from off-screen-right; account for the right margin
         * so the panel enters past the viewport edge with the gap
         * already factored in. */
        from { transform: translateX(calc(100% + var(--steward-flyout-margin))); }
        to   { transform: translateX(0); }
      }

      @keyframes flyoutSlideInLeft {
        /* Mirror of slideInRight for left-anchored flyouts. */
        from { transform: translateX(calc(-100% - var(--steward-flyout-margin))); }
        to   { transform: translateX(0); }
      }

      /* Mobile (full-screen) slide-ins. Same directional cue (panel
       * enters from the FAB's side), but no margin term — the panel
       * is 100vw on mobile, so the desktop's +12px overshoot would be
       * dead animation distance. The mobile-specific rules + keyframes
       * are paired by viewport in the @media (max-width: 640px) block
       * further down. */
      @keyframes flyoutSlideInRightMobile {
        from { transform: translateX(100%); }
        to   { transform: translateX(0); }
      }

      @keyframes flyoutSlideInLeftMobile {
        from { transform: translateX(-100%); }
        to   { transform: translateX(0); }
      }

      /* Inside the flyout, the panel chrome's children must fill the panel.
       * The classic .panel wrapper isn't rendered in flyout mode (the
       * popover IS the wrapper) — its inner children render directly under
       * the popover div. */
      :host([mode='flyout']) .flyout-panel > .control-row,
      :host([mode='flyout']) .flyout-panel > .messages-container,
      :host([mode='flyout']) .flyout-panel > steward-input {
        flex-shrink: 0;
      }
      :host([mode='flyout']) .flyout-panel > .messages-container {
        flex: 1;
        min-height: 0;
      }

      /* ── Mobile: full-screen overlay ── */
      /* Embedded mode (Phase 2) lives inline inside a host container — full-
       * screen mobile rules would force it to take over the viewport, which
       * defeats the inline use case. Exclude embedded from both rules. */
      @media (max-width: 640px) {
        :host(:not([mode='embedded'])) .panel {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          border-radius: 0;
          border: none;
        }

        /* Mobile collapses the flyout to full-screen too — same UX as the
         * classic panel on phones, just delivered via popover. dvh keeps
         * the input above the iOS keyboard. The desktop floating margin
         * is dropped here — at <=640px the gap eats too much usable
         * width. */
        :host([mode='flyout']) .flyout-panel {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          width: 100vw;
          max-width: 100vw;
          min-width: 0;
          height: 100vh;
          height: 100dvh;
          /* Override the desktop rule's max-height (which subtracts the
           * floating margin). Without this, height resolves to
           * 100dvh - 24px and the over-constrained position:fixed pins
           * the panel to top:0, leaving a gap at the bottom. */
          max-height: 100vh;
          max-height: 100dvh;
          border-radius: 0;
          border: none;
        }

        /* Mobile slide-in animations — no margin overshoot (the panel
         * is 100vw, so the desktop's +12px overshoot would waste ~3%
         * of the animation distance). Same per-position split as
         * desktop so the directional cue still matches the FAB side. */
        :host([mode='flyout'][position='bottom-right']) .flyout-panel:popover-open,
        :host([mode='flyout']:not([position='bottom-left'])) .flyout-panel:popover-open {
          animation: flyoutSlideInRightMobile 0.3s ease-out;
        }

        :host([mode='flyout'][position='bottom-left']) .flyout-panel:popover-open {
          animation: flyoutSlideInLeftMobile 0.3s ease-out;
        }

        /* Hide launcher when panel is open on mobile to prevent overlap */
        :host([open]:not([mode='embedded'])) .launcher-wrap {
          display: none;
        }
      }

      /* ── Image preview modal ── */
      /* Native-<dialog> path (preferred): top-layer rendering so the
       * preview stacks above the flyout dialog when both are open. */
      .img-modal-dialog {
        padding: 0;
        margin: 0;
        border: none;
        background: rgba(0, 0, 0, 0.85);
        max-width: 100vw;
        max-height: 100vh;
        width: 100vw;
        height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: steward-fade-in 0.15s ease;
        color: white;
      }
      .img-modal-dialog::backdrop {
        background: rgba(0, 0, 0, 0.85);
      }

      /* Fallback path: <div> overlay for engines without <dialog>. */
      .img-modal-backdrop {
        position: fixed;
        inset: 0;
        z-index: 10000;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: steward-fade-in 0.15s ease;
      }
      .img-modal-toolbar {
        position: absolute;
        top: 12px;
        right: 12px;
        display: flex;
        gap: 8px;
      }
      .img-modal-btn {
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.15);
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .img-modal-btn:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      .img-modal-btn svg {
        width: 18px;
        height: 18px;
      }
      .img-modal-img {
        max-width: calc(100vw - 48px);
        max-height: calc(100vh - 96px);
        object-fit: contain;
        border-radius: 4px;
      }
      .img-modal-expired {
        color: rgba(255, 255, 255, 0.7);
        font-size: 1.1em;
      }
      @keyframes steward-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      /* Respect users who request reduced motion (vestibular, motion-sensitive).
       * WCAG 2.3.3 calls out both animation AND transition; we suppress the
       * looping/entrance animations and the layout-changing transitions
       * (panel-expand, theme toggle slide) while keeping zero-impact 0.15s
       * background fades that don't trigger vestibular responses. */
      @media (prefers-reduced-motion: reduce) {
        .launcher-wrap--intro,
        .launcher,
        .launcher svg,
        .halo-throb,
        .halo-throb--intro,
        .panel,
        .img-modal-backdrop,
        :host([mode='flyout']) .flyout-panel:popover-open {
          animation: none !important;
        }
        .panel--expanded,
        .scroll-bottom-btn,
        .row-menu {
          transition: none !important;
        }
        /* Streaming under reduced-motion: animations are stripped, but
         * the user still needs a "thinking" visual cue. Pin the glow at
         * the intense end of the throb so streaming state is communicated
         * statically. */
        :host([streaming]) .halo-throb {
          box-shadow: 0 0 34px 9px rgba(var(--steward-halo-rgb), 0.7) !important;
        }
      }

      /* Respect users who request reduced transparency at the OS level
       * (iOS / macOS Accessibility → Reduce Transparency). Strip blur
       * and revert each glass surface to its solid base color. */
      @media (prefers-reduced-transparency: reduce) {
        .panel,
        :host([mode='flyout']) .flyout-panel,
        .header-actions,
        .launcher {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .panel,
        :host([mode='flyout']) .flyout-panel {
          background: var(--steward-bg) !important;
        }
        .header-actions {
          background: var(--steward-bg-secondary) !important;
        }
        .launcher {
          /* Strip the chip-tier glass; revert to the brand-primary
           * solid surface (the FAB's pre-glass treatment).
           * var(--focus-ring) stays at the head of the stack so
           * :focus-visible still glows. */
          background: var(--steward-primary) !important;
          border-color: transparent !important;
          box-shadow: var(--focus-ring), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
      }
    `,
  ];

  // ── Render ──

  render() {
    // Flyout mode (Popover API) always renders the panel element so
    // showPopover()/hidePopover() in updated() has a stable target — even
    // when closed. The panel chrome inside is conditional on _open so we
    // don't pay the template/render cost while closed. Other modes mount the
    // panel only when open (preserves the classic mode's exact behavior
    // bit-for-bit). On engines without Popover API, fall through to the
    // classic .panel so hosts on older WebKit/Gecko still get a working
    // flyout (positioned :fixed; high z-index — see CSS).
    const isFlyoutPopover = this.mode === 'flyout' && supportsPopover();
    return html`
      ${isFlyoutPopover
        ? this._renderFlyoutPanel()
        : this._open
          ? this._renderPanel()
          : nothing}
      ${this._renderLauncher()}
      ${this._renderImageModal()}
    `;
  }

  /** Non-modal side panel via the Popover API. Notes on what is intentionally
   *  absent:
   *    - NO @click backdrop dismissal — there is no backdrop. The host stays
   *      interactive behind the panel.
   *    - NO @close handler — popover doesn't fire native `close` events the
   *      way <dialog> does. ESC dismissal flows through our existing
   *      `_handleKeydown` (which calls `_toggle()`).
   *    - NO `aria-modal="true"` — that would lie to screen readers; this is
   *      a side panel, not a modal. `role="dialog"` alone is correct: it
   *      announces a labelled grouping without the modal trap semantics.
   *    - popover="manual" disables light-dismiss + the platform ESC binding,
   *      because we drive show/hide explicitly. */
  private _renderFlyoutPanel() {
    return html`
      <div
        class="flyout-panel"
        popover="manual"
        role="dialog"
        aria-label="${this.widgetTitle}"
      >${this._open ? this._renderPanelChrome() : nothing}</div>
    `;
  }

  private _renderLauncher() {
    // The FAB launcher renders in classic and flyout. Two ways to suppress
    // it: embedded mode (FAB-less by design — host owns the trigger), or
    // the explicit `hide-launcher` attribute on classic/flyout (host wants
    // its own trigger while keeping the floating panel). Either way the
    // public open()/close()/toggle() API is the contract for the host's
    // own button. See README "Embed mode" guide.
    if (this.mode === 'embedded' || this.hideLauncher) return nothing;
    const label = this._open ? `Close ${this.widgetTitle}` : `Open ${this.widgetTitle}`;
    return html`
      <div class="launcher-wrap">
        <div class="halo-throb"></div>
        <button
          class="launcher"
          @click=${this._toggle}
          @mouseenter=${this._cancelIntro}
          @focus=${this._cancelIntro}
          aria-label="${label}"
        >
          <svg viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="steward-gl" x1="0.5" y1="0" x2="0.5" y2="1">
                <stop offset="0%" stop-color="var(--steward-svg-grad-left-edge)" />
                <stop offset="50%" stop-color="var(--steward-svg-grad-left-mid)" />
                <stop offset="100%" stop-color="var(--steward-svg-grad-left-edge)" />
              </linearGradient>
              <linearGradient id="steward-gr" x1="0.5" y1="0" x2="0.5" y2="1">
                <stop offset="0%" stop-color="var(--steward-svg-grad-right-edge)" />
                <stop offset="50%" stop-color="var(--steward-svg-grad-right-mid)" />
                <stop offset="100%" stop-color="var(--steward-svg-grad-right-edge)" />
              </linearGradient>
            </defs>
            <path d="M100,10 C95,70 70,95 10,100 C70,105 95,130 100,190 L100,100 Z" fill="url(#steward-gl)" />
            <path d="M100,10 C105,70 130,95 190,100 C130,105 105,130 100,190 L100,100 Z" fill="url(#steward-gr)" />
          </svg>
        </button>
      </div>
    `;
  }

  private _renderPanel() {
    // Embedded mode is non-modal inline content, not a dialog: assistive
    // tech that hears `role="dialog"` will announce the panel as a modal
    // popup and constrain ARIA semantics accordingly (focus expectations,
    // dismissal cues). `role="region"` correctly describes a labelled
    // landmark that is part of the host page's reading order. Classic
    // (FAB-popup) keeps `role="dialog"` because it IS modal-ish (focus
    // trap + body lock).
    const role = this.mode === 'embedded' ? 'region' : 'dialog';
    return html`
      <div class="panel ${this._expanded ? 'panel--expanded' : ''}" role="${role}" aria-label="${this.widgetTitle}">
        ${this._renderPanelChrome()}
      </div>
    `;
  }

  /** Shared chrome (window controls + error + messages + scroll button +
   *  sr-live + input + control row) for both the classic .panel container and
   *  the flyout popover. The outer container differs, but the inner contents
   *  are identical.
   *
   *  A list of pieces rather than one template, so that no child binding
   *  sits at a template's top level: happy-dom's parser drops a top-level
   *  child marker, shifting every later binding onto the wrong node. Each
   *  conditional piece is its own entry; the containers render the list
   *  inside their element, which a browser lays out exactly as one
   *  template. */
  private _renderPanelChrome() {
    const hasMessages = this._messages.length > 0;
    const themeLabel = this._darkMode ? 'Switch to light mode' : 'Switch to dark mode';
    const themeTip = this._darkMode ? 'Light Mode' : 'Dark Mode';
    return [
      html`
        <div class="header-actions window-controls" role="group" aria-label="Window">
          ${this.mode === 'classic'
            ? html`
          <button class="header-btn" @click=${this._toggleExpand} aria-label="${this._expanded ? 'Collapse' : 'Expand'}" data-tip="${this._expanded ? 'Collapse' : 'Expand'}" data-tip-pos="below">
            ${this._expanded
              ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/></svg>`
              : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>`}
          </button>`
            : nothing}
          <button
            class="header-btn"
            @click=${this._toggle}
            aria-label="Close"
            data-tip="Close"
            data-tip-pos="below"
            data-tip-anchor="end"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      `,
      this._error
        ? html`<div class="error-banner">${this._error}</div>`
        : nothing,
      html`
        <div class="messages-container" role="log" aria-live="polite" aria-busy="${this._isStreaming}" @scroll=${this._onMessagesScroll}>
          ${this._disclosureAtHead
            ? html`<p class="ai-disclosure ai-disclosure--head">${AI_DISCLOSURE}</p>`
            : nothing}
          ${this._messages.length === 0
            ? html`
                <div class="welcome">
                  <svg class="welcome-icon" viewBox="0 0 200 200" fill="none" aria-hidden="true" width="48" height="48">
                    <defs>
                      <linearGradient id="steward-wl" x1="0.5" y1="0" x2="0.5" y2="1">
                        <stop offset="0%" stop-color="var(--steward-svg-grad-left-edge)" />
                        <stop offset="50%" stop-color="var(--steward-svg-grad-left-mid)" />
                        <stop offset="100%" stop-color="var(--steward-svg-grad-left-edge)" />
                      </linearGradient>
                      <linearGradient id="steward-wr" x1="0.5" y1="0" x2="0.5" y2="1">
                        <stop offset="0%" stop-color="var(--steward-svg-grad-right-edge)" />
                        <stop offset="50%" stop-color="var(--steward-svg-grad-right-mid)" />
                        <stop offset="100%" stop-color="var(--steward-svg-grad-right-edge)" />
                      </linearGradient>
                    </defs>
                    <path d="M100,10 C95,70 70,95 10,100 C70,105 95,130 100,190 L100,100 Z" fill="url(#steward-wl)" />
                    <path d="M100,10 C105,70 130,95 190,100 C130,105 105,130 100,190 L100,100 Z" fill="url(#steward-wr)" />
                  </svg>
                  ${this.widgetTitle ? html`<h3>${this.widgetTitle}</h3>` : nothing}
                  ${this.tagline ? html`<p>${this.tagline}</p>` : nothing}
                  <p class="ai-disclosure">${AI_DISCLOSURE}</p>
                </div>
              `
            : this._messages.map((msg) => this._renderMessage(msg))}
          ${this._messages.length === 0 ? this._renderStarters() : nothing}
        </div>
      `,
      this._showScrollBtn
        ? html`
              <button
                class="scroll-bottom-btn${this._grewWhileAway ? ' has-new' : ''}"
                @click=${() => {
                  this._grewWhileAway = false;
                  this._scrollToBottom(true);
                }}
                aria-label=${this._grewWhileAway
                  ? 'New text below — scroll to bottom'
                  : 'Scroll to bottom'}
              >
                ${this._grewWhileAway
                  ? html`<span class="scroll-bottom-label">New text</span>`
                  : nothing}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            `
        : nothing,
      html`
        <span class="sr-only" aria-live="polite"
          >${this._isStreaming ? 'Assistant is typing...' : ''}</span
        >

        <steward-input
          .placeholder=${this.placeholder}
          ?streaming=${this._isStreaming}
          ?cancelling=${this._cancelRequested}
          @steward-submit=${this._handleSubmit}
          @steward-stop=${this._handleStop}
        ></steward-input>

        <div class="control-row">
          ${hasMessages ? html`
            <div class="row-group" role="group" aria-label="Conversation">
              <button
                class="header-btn new-btn"
                @click=${this._newConversation}
                aria-label="New conversation"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="12" y1="7" x2="12" y2="13"/></svg>
                <span aria-hidden="true">New</span>
              </button>
              ${this._renderMoreControl()}
            </div>
          ` : nothing}
          <span class="row-spacer"></span>
          <div class="row-group" role="group" aria-label="Display">
            ${this._renderThinkingControl()}
            <button class="header-btn" @click=${this._toggleTheme} aria-label="${themeLabel}" data-tip="${themeTip}" data-tip-anchor="end">
              ${this._darkMode
                ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>`
                : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>`}
            </button>
          </div>
        </div>
      `,
    ];
  }

  /** The operator's starter questions, under the welcome card. Each row
   *  is a button named by its question, with its hint as the description;
   *  the glyph and the arrow are decoration. Glyphs are all or none. */
  private _renderStarters() {
    const starters = normalizeStarters(this.starters);
    if (starters.length === 0) return nothing;
    const glyphs = startersShowGlyphs(starters);
    return html`
      <div class="starters" role="group" aria-label="Suggested questions">
        ${starters.map((s: Starter, i) => html`
          <button
            class="starter"
            type="button"
            aria-label=${s.question}
            aria-describedby=${s.hint ? `starter-hint-${i}` : nothing}
            @click=${() => this._sendStarter(s.question)}
          >
            ${glyphs
              ? html`<span class="starter-glyph" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${starterGlyph(s.icon)}</svg></span>`
              : nothing}
            <span class="starter-text">
              <span class="starter-question">${s.question}</span>
              ${s.hint ? html`<span class="starter-hint" id="starter-hint-${i}">${s.hint}</span>` : nothing}
            </span>
            <span class="starter-arrow" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ROW_ARROW}</svg></span>
          </button>
        `)}
      </div>
    `;
  }

  /** A starter row sends its question as the composer sends typed text:
   *  the same `steward-submit`, dispatched from the composer, with no
   *  `programmatic` flag. Guarded on a turn already streaming, so a second
   *  tap in the same frame (before the rows unmount) tells the host
   *  nothing that did not happen. Focus then goes where a typed send
   *  leaves it: the field on a fine pointer; on a coarse one it is let go,
   *  as the composer does, so the answer is not hidden under a keyboard. */
  private _sendStarter(question: string): void {
    const input = this._inputEl;
    if (!input || this._isStreaming) return;
    input.dispatchEvent(
      new CustomEvent('steward-submit', {
        detail: { message: question },
        bubbles: true,
        composed: true,
      }),
    );
    const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (!coarse) void this.updateComplete.then(() => this._inputEl?.focusInput());
  }

  private _renderMessage(msg: Message) {
    // Sources accompany a rendered answer bubble. A turn stopped before any
    // answer text (cancelled with empty content) renders no bubble — the bubble
    // shows only when content||streaming — so its sources have nothing to attach
    // to. Requiring content (sources already require !streaming) keeps the
    // "Show documents found" affordance paired with an actual answer.
    const hasSources =
      msg.content && msg.sources && msg.sources.length > 0 && !msg.streaming;
    const hasThinking =
      msg.role === 'assistant' &&
      msg.thinkingSteps !== undefined &&
      msg.thinkingSteps.length > 0;
    // Thumbs only on a completed model answer: the trace id is stored solely
    // from the terminal `done` of a finished turn (a cancelled turn finalizes
    // before it; a turn whose answer the output guardrail replaced drops it),
    // so its presence — plus the streaming/cancelled guards for restored edge
    // cases — is the gate.
    const hasFeedback =
      msg.role === 'assistant' && !msg.streaming && !msg.cancelled && !!msg.traceId;
    return html`
      <div class="message-group ${hasSources ? 'has-sources' : ''}">
        ${hasThinking
          ? html`<steward-thinking
              .steps=${msg.thinkingSteps!}
              ?active=${msg.streaming ?? false}
              ?cancelling=${(msg.streaming ?? false) && this._cancelRequested}
              .totalMs=${msg.elapsedMs}
              .mode=${this._thinkingMode}
            ></steward-thinking>`
          : nothing}
        ${msg.content || msg.streaming
          ? html`<steward-message
              .role=${msg.role}
              .content=${msg.content}
              .blocks=${msg.blocks ?? null}
              .aiGenerated=${msg.aiGenerated === true}
              ?streaming=${msg.streaming ?? false}
              ?revealing=${msg.streaming ?? false}
              .artifacts=${this._artifacts}
            ></steward-message>`
          : nothing}
        ${msg.cancelled ? html`<div class="msg-note">Stopped.</div>` : nothing}
        ${hasFeedback
          ? html`
              <div class="feedback-row">
                <button
                  class="feedback-btn ${msg.feedback === 'up' ? 'selected' : ''}"
                  aria-label="Good answer"
                  aria-pressed=${msg.feedback === 'up' ? 'true' : 'false'}
                  @click=${() => this._submitFeedback(msg.id, 'up')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>
                </button>
                <button
                  class="feedback-btn ${msg.feedback === 'down' ? 'selected' : ''}"
                  aria-label="Poor answer"
                  aria-pressed=${msg.feedback === 'down' ? 'true' : 'false'}
                  @click=${() => this._submitFeedback(msg.id, 'down')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>
                </button>
              </div>
            `
          : nothing}
      </div>
      ${hasSources
        ? html`
            ${this._expandedSources.has(msg.id)
              ? html`
                  <div class="sources">
                    ${msg.sources!.map(
                      (s) => html`
                        <span
                          class="source-tag"
                          title="${s.headingPath || s.title}"
                          >${s.title}</span
                        >
                      `,
                    )}
                  </div>
                  <button
                    class="sources-toggle"
                    @click=${() => this._toggleSources(msg.id)}
                  >Hide documents found</button>
                `
              : html`
                  <button
                    class="sources-toggle"
                    @click=${() => this._toggleSources(msg.id)}
                  >Show documents found (${msg.sources!.length})</button>
                `}
          `
        : nothing}
    `;
  }

  // ── Event handlers ──

  private _handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // Don't steal ESC from IME composition (CJK / Korean / Vietnamese
      // candidate-window dismissal) — closing the widget mid-composition
      // would lose the user's in-flight message.
      if (e.isComposing || (e as KeyboardEvent & { keyCode?: number }).keyCode === 229) {
        return;
      }
      // Image preview is a true modal regardless of mode — close it first.
      if (this._previewSrc) {
        e.preventDefault();
        this._closePreview();
        return;
      }
      // Embedded mode is non-modal — the host owns ESC semantics. A user
      // pressing ESC inside the host's own UI (close a host menu, exit a
      // host dialog) must not also close the embedded chat. The widget
      // stays open until the host calls .close() imperatively.
      if (this.mode === 'embedded') return;
      if (this._open) {
        e.preventDefault();
        this._toggle();
      }
    }
  }

  // ── Image preview modal ──

  private _closePreview(): void {
    this._previewSrc = null;
    this._previewDownloadUrl = null;
  }

  private _onPreviewError(): void {
    this._previewExpired = true;
  }

  private _downloadImage(): void {
    const url = this._previewDownloadUrl || this._previewSrc;
    if (!url) return;
    window.open(url, '_blank');
  }

  private _renderImageModal() {
    if (!this._previewSrc) return nothing;
    const useNative = supportsNativeDialog();
    const inner = html`
      <div class="img-modal-toolbar" @click=${(e: Event) => e.stopPropagation()}>
        <button class="img-modal-btn" @click=${this._downloadImage} aria-label="Download image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <button class="img-modal-btn" @click=${this._closePreview} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      ${this._previewExpired
        ? html`<div class="img-modal-expired">This chart has expired.</div>`
        : html`<img class="img-modal-img"
                     src=${this._previewSrc}
                     alt=${this._previewAlt}
                     @error=${this._onPreviewError}
                     @click=${(e: Event) => e.stopPropagation()} />`}
    `;
    if (useNative) {
      // <dialog> places this in the top-layer so it stacks above the flyout
      // (also a top-layer dialog) when both are open. Backdrop click and
      // ESC dismissal mirror the flyout pattern.
      return html`
        <dialog
          class="img-modal-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          @click=${this._onImgDialogClick}
          @close=${this._onImgDialogClose}
        >${inner}</dialog>
      `;
    }
    // Fallback for engines without <dialog>: existing div-based modal.
    return html`
      <div class="img-modal-backdrop"
           @click=${this._closePreview}
           role="dialog"
           aria-modal="true"
           aria-label="Image preview">
        ${inner}
      </div>
    `;
  }

  /** Click on the image dialog's ::backdrop — browser models backdrop
   *  clicks as clicks on the dialog element itself. */
  private _onImgDialogClick(e: MouseEvent): void {
    if (e.target === this._imgDialog) {
      this._closePreview();
    }
  }

  /** Sync `_previewSrc` when the native dialog dispatches close (ESC,
   *  programmatic close). */
  private _onImgDialogClose(): void {
    if (this._previewSrc) {
      this._closePreview();
    }
  }

  private _cancelIntro(): void {
    if (this._introTimer != null) {
      clearTimeout(this._introTimer);
      this._introTimer = null;
    }
    this.shadowRoot?.querySelector('.halo-throb')?.classList.remove('halo-throb--intro');
    this.shadowRoot?.querySelector('.launcher-wrap')?.classList.remove('launcher-wrap--intro');
  }

  /** Resolve [chart:id] placeholders in content using the artifacts Map. */
  private _resolveChartPlaceholders(content: string): string {
    return resolveChartPlaceholders(content, this._artifacts);
  }

  private _buildMarkdown(): string {
    const timestamp = new Date().toISOString();
    const lines: string[] = [
      `# STEWARD Conversation`,
      `_Exported ${timestamp}_`,
      '',
    ];
    for (const msg of this._messages) {
      if (msg.streaming) continue;
      lines.push('---');
      lines.push('');
      // The model's text carries its mark into the file, after its label.
      const mark = msg.aiGenerated === true ? ' _(AI-generated)_' : '';
      if (msg.role === 'user') {
        lines.push(`**You:**\n${msg.content}`);
      } else if (msg.role === 'assistant') {
        const resolved = this._resolveChartPlaceholders(msg.content);
        lines.push(`**STEWARD:**${mark}\n${resolved}`);
        if (msg.sources && msg.sources.length > 0) {
          lines.push('');
          lines.push('_Documents found:_');
          // By title alone: the stored filename carries an id suffix
          // (`name_<id8>.md`) no reader should see; the server sends a
          // document's own name — a Drive file's Drive name — as `title`.
          for (const s of msg.sources) {
            lines.push(`- ${s.title}`);
          }
        }
      } else if (msg.role === 'error') {
        lines.push(`**Error:**${mark}\n${msg.content}`);
      }
      lines.push('');
    }
    return lines.join('\n');
  }

  private _buildJson(): string {
    const messages = this._messages
      .filter((m) => !m.streaming)
      .map((m) => {
        const content = m.role === 'assistant'
          ? this._resolveChartPlaceholders(m.content)
          : m.content;
        const entry: Record<string, unknown> = {
          role: m.role,
          content,
          // On every message, so a reader never has to infer it from an
          // absent key.
          aiGenerated: m.aiGenerated === true,
        };
        if (m.sources && m.sources.length > 0) {
          entry.sources = m.sources;
        }
        return entry;
      });
    return JSON.stringify(
      {
        conversationId: this._conversationId,
        exportedAt: new Date().toISOString(),
        messageCount: messages.length,
        messages,
      },
      null,
      2,
    );
  }

  private async _copyConversation(): Promise<void> {
    if (this._copyFeedbackTimer != null) {
      clearTimeout(this._copyFeedbackTimer);
    }
    try {
      const md = this._buildMarkdown();
      await navigator.clipboard.writeText(md);
      this._copyFeedback = 'copied';
    } catch (e) {
      console.warn('[steward-chat] Failed to copy conversation to clipboard:', e);
      this._copyFeedback = 'failed';
    }
    this._copyFeedbackTimer = window.setTimeout(() => {
      this._copyFeedback = 'idle';
      this._copyFeedbackTimer = null;
    }, 1500);
  }

  private _exportConversation(format: 'md' | 'json'): void {
    const content = format === 'md' ? this._buildMarkdown() : this._buildJson();
    const mimeType = format === 'md' ? 'text/markdown' : 'application/json';
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const filename = `steward-${dateStr}.${format}`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    this._copyFeedback = 'idle';
  }

  /** Stop the in-flight turn (the send button's Stop affordance). The
   *  turn-boundary server cancel is the primary mechanism; the client only
   *  backstops the gaps it structurally cannot cover. The split turns on whether
   *  the answer has begun streaming (`_deliveryStarted`, set on the first token):
   *
   *  - Pre-token (still thinking): the graph is running server-side, so fire the
   *    server cancel — the synthesizer/tool-executor poll the flag at the next
   *    round boundary and stop gracefully. NOT a client abort here, so a
   *    multi-round turn stops cleanly server-side. If the turn is single-round
   *    (no second boundary) the server can't act and streams the answer anyway;
   *    the `onToken` backstop catches that and finalizes client-side.
   *  - Mid-stream: the whole guardrail-passed answer is already buffered on the
   *    client and the visible motion is our own typewriter draining the reveal buffer
   *    (the server flushed everything in ~50ms and has moved on). There is nothing
   *    left to ask the server for, so stop locally and immediately.
   *
   *  The `_conversationId` guard intentionally no-ops a Stop pressed in the
   *  sub-RTT window before the stream's first `conversation` event arrives. The
   *  button is not gated on `_conversationId` (it would flicker disabled→enabled
   *  at stream start); a no-op there is preferable for that imperceptible window. */
  private _handleStop(): void {
    if (!this._isStreaming || !this._conversationId) return;
    if (this._deliveryStarted) {
      this._stopTurnLocally();
    } else {
      this._client?.cancel(this._conversationId);
      this._cancelRequested = true;
    }
  }

  /** Abandon an in-flight turn locally and immediately. Two callers: the
   *  mid-stream Stop (`_handleStop`), where the answer is already buffered and
   *  this freezes the typewriter at the current character; and the single-round
   *  backstop (`onToken`), where a pre-token cancel the server couldn't honor is
   *  finalized as the answer begins to stream. Either way: cancel the drip, drop
   *  the un-shown remainder, mark the turn stopped, re-enable input, and abort
   *  the stream. `_turnStopped` neutralizes any trailing `token`/`done` still
   *  buffered in the transport when the abort lands. The completed answer may
   *  remain in server history — a client stop halts the display, not the
   *  (already-generated) work; see design D4. */
  private _stopTurnLocally(): void {
    this._turnStopped = true;
    // Freeze the typewriter: cancel the pending frame and discard the un-shown
    // buffer so the text stops exactly where the user saw it.
    if (this._revealRafId != null) {
      cancelAnimationFrame(this._revealRafId);
      this._revealRafId = null;
    }
    this._revealSource = this._revealSource.slice(0, this._revealCursor);
    this._revealActive = false;
    // Drop the deferred finalize a not-yet-drained `done` may have parked here,
    // so it cannot run after the freeze.
    this._onRevealDone = null;
    const msgId = this._revealMsgId;
    this._revealMsgId = null;
    const elapsedMs = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : 0) - this._turnStartMs,
    );
    if (msgId) {
      this._updateMessage(msgId, (msg) => {
        msg.streaming = false;
        msg.cancelled = true;
        // No server `cancelled` ack on this path; this is the collapsed-timeline
        // aggregate source, matching the elapsedMs a normal turn's `done` carries.
        msg.elapsedMs = elapsedMs;
      });
    }
    this._isStreaming = false;
    this._cancelRequested = false;
    this._saveSession();
    // Stop reading the rest of the (finished) stream. The client swallows the
    // resulting AbortError, so no error surfaces.
    this._client?.abort();
    this.updateComplete.then(() => {
      if (this.shadowRoot?.contains(this.shadowRoot?.activeElement)) {
        this._inputEl?.focusInput();
      }
    });
  }

  private _newConversation(): void {
    // Starting a fresh conversation abandons any in-flight turn. Tell the
    // server to cancel it first (frees the slot / stops token burn) — a
    // bare client abort leaves the server running the turn to its deadline —
    // then abort the local stream and reset state.
    if (this._isStreaming && this._conversationId) {
      this._client?.cancel(this._conversationId);
    }
    // Abort any in-flight stream BEFORE touching state. Without this the
    // abandoned stream keeps arriving in the background, `_isStreaming`
    // stays true, the composer's send stays a Stop, and the user cannot
    // send for the remainder of the SSE response.
    // Also fires ghost `steward-token` / `steward-message` events into the
    // host's analytics. Mirror disconnectedCallback's cleanup order.
    this._client?.abort();
    // The ⋯ menu unmounts with the conversation. A screen reader can reach
    // New with a click and no pointerdown, so the outside-click dismiss may
    // never have run: close it here, or it reopens with the next one.
    this._closeMoreMenu();
    this._flushReveal();
    this._isStreaming = false;
    this._cancelRequested = false;
    this._messages = [];
    this._disclosureAtHead = false;
    this._conversationId = null;
    this._error = null;
    this._expandedSources = new Set();
    this._artifacts = new Map();
    this._previewSrc = null;
    this._previewAlt = '';
    this._previewDownloadUrl = '';
    this._previewExpired = false;
    try {
      sessionStorage.removeItem(this._sessionKey);
    } catch (e) {
      console.warn('[steward-chat] Failed to clear session from sessionStorage:', e);
    }
  }

  /** Optimistic thumbs handler: set the state, fire the POST, revert on
   *  rejection (quiet failure — the conversation is never interrupted, no
   *  user-facing error). Re-clicking the selected thumb is a no-op;
   *  clicking the other thumb switches — the server upserts one
   *  `user-feedback` score per trace, latest wins. */
  private _submitFeedback(msgId: string, value: 'up' | 'down'): void {
    const msg = this._messages.find((m) => m.id === msgId);
    if (!msg?.traceId || !this._conversationId) return;
    if (msg.feedback === value) return;
    const previous = msg.feedback;
    const traceId = msg.traceId;
    this._updateMessage(msgId, (m) => {
      m.feedback = value;
    });
    this._saveSession();
    // A throwaway client, NOT _getClient(): that swaps this._client, and a
    // thumb clicked on an older answer while a new turn is streaming would
    // detach the live stream from abort()/Stop. sendFeedback holds no
    // stream state, so a fresh instance (current keys) is the safe path.
    const endpoint = this._resolveEndpoint();
    if (endpoint === null) {
      // No network by contract — settle the optimistic thumb back.
      this._updateMessage(msgId, (m) => {
        if (m.feedback === value) {
          m.feedback = previous;
        }
      });
      this._saveSession();
      return;
    }
    const client = new StewardStreamClient(endpoint, this.apiKey, this.authToken);
    client.sendFeedback(this._conversationId, traceId, value).catch((e) => {
      console.warn('[steward-chat] Feedback request failed; reverting:', e);
      this._updateMessage(msgId, (m) => {
        // Revert only if this request's optimistic value is still the
        // visible state. A newer click owns the thumb once it lands its
        // own optimistic value — a stale failure settling after a switch
        // must not clobber it (server-side the newer POST wins anyway).
        if (m.feedback === value) {
          m.feedback = previous;
        }
      });
      this._saveSession();
    });
  }

  private _toggleSources(msgId: string): void {
    const next = new Set(this._expandedSources);
    if (next.has(msgId)) {
      next.delete(msgId);
    } else {
      next.add(msgId);
    }
    this._expandedSources = next;
    this.updateComplete.then(() => this._checkScrollOverflow());
  }

  // ── The row's menus: Thinking display and ⋯ ──
  /** Outside-click dismiss for an open menu. A document-level pointerdown
   *  (added only while that menu is open) closes it unless the click landed
   *  inside THIS instance's control. composedPath crosses the shadow
   *  boundary, so the widget's own control element is in the path for an
   *  inside click; the exact element is matched (not the class) so a second
   *  widget on the page can't keep this one open. */
  private _onThinkingDocPointerDown = (e: Event): void => {
    const control = this.renderRoot.querySelector('.thinking-control');
    if (!control || !e.composedPath().includes(control)) this._closeThinkingMenu();
  };

  private _onMoreDocPointerDown = (e: Event): void => {
    const control = this.renderRoot.querySelector('.more-control');
    if (!control || !e.composedPath().includes(control)) this._closeMoreMenu();
  };

  private _toggleThinkingMenu(): void {
    if (this._thinkingMenuOpen) {
      this._closeThinkingMenu();
      return;
    }
    this._closeMoreMenu();
    this._thinkingMenuOpen = true;
    document.addEventListener('pointerdown', this._onThinkingDocPointerDown);
    this._focusMenuItem('#thinking-flyout');
  }

  private _closeThinkingMenu(returnFocus = false): void {
    if (!this._thinkingMenuOpen) return;
    this._thinkingMenuOpen = false;
    document.removeEventListener('pointerdown', this._onThinkingDocPointerDown);
    if (returnFocus) this._focusTrigger('.thinking-trigger');
  }

  private _toggleMoreMenu(): void {
    if (this._moreMenuOpen) {
      this._closeMoreMenu();
      return;
    }
    this._closeThinkingMenu();
    this._moreMenuOpen = true;
    document.addEventListener('pointerdown', this._onMoreDocPointerDown);
    this._focusMenuItem('#more-menu');
  }

  private _closeMoreMenu(returnFocus = false): void {
    if (!this._moreMenuOpen) return;
    this._moreMenuOpen = false;
    document.removeEventListener('pointerdown', this._onMoreDocPointerDown);
    if (returnFocus) this._focusTrigger('.more-trigger');
  }

  /** Opening a menu moves focus into it: onto the checked item when there
   *  is one, else the first. After the update, since a closed menu is
   *  visibility:hidden and cannot take focus. */
  private _focusMenuItem(menuSelector: string): void {
    void this.updateComplete.then(() => {
      const menu = this.renderRoot.querySelector(menuSelector);
      const target =
        menu?.querySelector<HTMLElement>('.menu-item[aria-checked="true"]') ??
        menu?.querySelector<HTMLElement>('.menu-item:not(:disabled)');
      target?.focus();
    });
  }

  private _focusTrigger(selector: string): void {
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector<HTMLElement>(selector)?.focus();
    });
  }

  /** An open row menu's keys. Escape closes it and returns focus to its
   *  trigger, and stops there: the panel's own document-level Escape would
   *  otherwise close the whole panel with it. The arrows (and Home/End)
   *  move among the enabled items; Tab closes the menu and lets focus move
   *  on. A closed menu takes no keys, so Escape on its trigger still closes
   *  the panel. */
  private _onMenuKeydown(e: KeyboardEvent, which: 'thinking' | 'more'): void {
    const open = which === 'thinking' ? this._thinkingMenuOpen : this._moreMenuOpen;
    if (!open) return;
    const close = (returnFocus: boolean): void =>
      which === 'thinking' ? this._closeThinkingMenu(returnFocus) : this._closeMoreMenu(returnFocus);
    if (e.key === 'Escape') {
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const menu = (e.currentTarget as HTMLElement).querySelector('.row-menu');
    const items = Array.from(menu?.querySelectorAll<HTMLElement>('.menu-item:not(:disabled)') ?? []);
    if (items.length === 0) return;
    e.preventDefault();
    const at = items.indexOf(this.shadowRoot?.activeElement as HTMLElement);
    const next =
      e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
      : e.key === 'ArrowDown' ? (at + 1) % items.length
      // From outside the items (focus still on the trigger), up is the last.
      : at < 0 ? items.length - 1
      : (at - 1 + items.length) % items.length;
    items[next].focus();
  }

  /** Selecting a mode updates `_thinkingMode` (which flows to
   *  `<steward-thinking>.mode` and re-renders the live timeline), morphs the
   *  trigger to that mode's glyph, closes the menu and returns focus. */
  private _selectThinkingMode(mode: 'full' | 'step' | 'hidden'): void {
    this._thinkingMode = mode;
    this._persistThinkingMode();
    this._closeThinkingMenu(true);
  }

  private _thinkingLabel(mode: 'full' | 'step' | 'hidden'): string {
    return mode === 'full'
      ? 'Full timeline'
      : mode === 'step'
        ? 'Current step'
        : 'Hidden';
  }

  /** Single source for each mode's glyph — used by both the morphing trigger
   *  (which shows the active mode) and the picker strip (which shows all three),
   *  so the trigger and its matching strip item never diverge. */
  private _thinkingIcon(mode: 'full' | 'step' | 'hidden'): TemplateResult {
    if (mode === 'full') {
      return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h11" /></svg>`;
    }
    if (mode === 'step') {
      return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none" /><path d="M10 12h10" /></svg>`;
    }
    return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18M10.6 10.7a2 2 0 0 0 2.7 2.8M9.9 5.1A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7a12.3 12.3 0 0 1-2.2 3M6.1 6.3C3.8 7.6 2 9.9 2 12c1 2.5 5 7 10 7 1 0 1.9-.2 2.8-.5" /></svg>`;
  }

  /** The Thinking display control: a round trigger in the row that morphs
   *  to the active mode's glyph, and a menu of all three modes that opens
   *  upward. The menu names each mode in words and marks the active one
   *  with a check, a heavier weight and aria-checked — never colour alone. */
  private _renderThinkingControl(): TemplateResult {
    const modes: Array<'full' | 'step' | 'hidden'> = ['full', 'step', 'hidden'];
    const expanded = this._thinkingMenuOpen;
    const activeLabel = this._thinkingLabel(this._thinkingMode);
    return html`
      <div
        class="menu-control thinking-control ${expanded ? 'is-expanded' : ''}"
        @keydown=${(e: KeyboardEvent) => this._onMenuKeydown(e, 'thinking')}
      >
        <button
          class="header-btn thinking-trigger"
          aria-haspopup="menu"
          aria-expanded=${String(expanded)}
          aria-controls="thinking-flyout"
          aria-label="Thinking - ${activeLabel}"
          data-tip=${expanded ? nothing : `Thinking - ${activeLabel}`}
          data-tip-anchor="end"
          @click=${() => this._toggleThinkingMenu()}
        >
          ${this._thinkingIcon(this._thinkingMode)}
        </button>
        <div id="thinking-flyout" class="row-menu thinking-flyout" role="menu" aria-label="Thinking display">
          ${modes.map((mode) => {
            const active = this._thinkingMode === mode;
            const label = this._thinkingLabel(mode);
            return html`
              <button
                class="menu-item thinking-btn ${active ? 'thinking-btn--active' : ''}"
                role="menuitemradio"
                aria-checked=${String(active)}
                aria-label=${label}
                tabindex=${expanded ? 0 : -1}
                @click=${() => this._selectThinkingMode(mode)}
              >
                ${this._thinkingIcon(mode)}
                <span>${label}</span>
                ${active
                  ? html`<svg class="menu-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>`
                  : nothing}
              </button>
            `;
          })}
        </div>
      </div>
    `;
  }

  /** The ⋯ menu: copy and the two exports, the Conversation pill's other
   *  three buttons. Each keeps its header button's accessible name and
   *  action, stays unavailable while an answer streams, and Copy keeps its
   *  feedback (Copied! / Copy failed) on its own item. */
  private _renderMoreControl(): TemplateResult {
    const expanded = this._moreMenuOpen;
    const copyLabel = this._copyFeedback === 'copied'
      ? 'Copied!'
      : this._copyFeedback === 'failed'
        ? 'Copy failed - try again'
        : 'Copy conversation';
    return html`
      <div
        class="menu-control more-control ${expanded ? 'is-expanded' : ''}"
        @keydown=${(e: KeyboardEvent) => this._onMenuKeydown(e, 'more')}
      >
        <button
          class="header-btn more-trigger"
          aria-haspopup="menu"
          aria-expanded=${String(expanded)}
          aria-controls="more-menu"
          aria-label="More conversation actions"
          data-tip=${expanded ? nothing : 'More'}
          data-tip-anchor="start"
          @click=${() => this._toggleMoreMenu()}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>
        </button>
        <div id="more-menu" class="row-menu" role="menu" aria-label="Conversation actions">
          <button
            class="menu-item ${this._copyFeedback === 'failed' ? 'menu-item--error' : ''}"
            role="menuitem"
            aria-label="${copyLabel}"
            tabindex=${expanded ? 0 : -1}
            ?disabled=${this._isStreaming}
            @click=${this._copyConversation}
          >
            ${this._copyFeedback === 'copied'
              ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>`
              : this._copyFeedback === 'failed'
                ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>`
                : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>`}
            <span>${copyLabel}</span>
          </button>
          <button
            class="menu-item"
            role="menuitem"
            aria-label="Export as Markdown"
            tabindex=${expanded ? 0 : -1}
            ?disabled=${this._isStreaming}
            @click=${() => { this._exportConversation('md'); this._closeMoreMenu(true); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
            <span>Export as Markdown</span>
          </button>
          <button
            class="menu-item"
            role="menuitem"
            aria-label="Export as JSON"
            tabindex=${expanded ? 0 : -1}
            ?disabled=${this._isStreaming}
            @click=${() => { this._exportConversation('json'); this._closeMoreMenu(true); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M10 13l-2 2 2 2M14 13l2 2-2 2" stroke-linecap="round"/></svg>
            <span>Export as JSON</span>
          </button>
        </div>
      </div>
    `;
  }

  private _toggleTheme(): void {
    this._darkMode = !this._darkMode;
    this.theme = this._darkMode ? 'dark' : 'light';
  }

  /** Force WebKit to re-raster the window controls' cached backdrop-filter layer
   *  after a theme change. The palette is applied via a constructable
   *  stylesheet (CSSOM), which changes the color the glass should sample but
   *  does not invalidate the pill's composited layer, so the pre-theme tint
   *  persists until an unrelated repaint. Holding `recompositing` for a single
   *  painted frame appends brightness(1.001) to the pill's filter list (see the
   *  :host([recompositing]) rule), mutating the filter itself — which does
   *  invalidate the layer — then drops it. Attribute toggle, not inline style,
   *  so it stays clean under strict style-src CSPs. The pending clear is
   *  cancelled and rescheduled on each call so a rapid re-toggle still holds the
   *  nudge for a full frame rather than clearing early. */
  private _pulseRecomposite(): void {
    this.setAttribute('recompositing', '');
    cancelAnimationFrame(this._recompositeRaf);
    this._recompositeRaf = requestAnimationFrame(() => {
      this._recompositeRaf = requestAnimationFrame(() =>
        this.removeAttribute('recompositing'),
      );
    });
  }

  private _toggleExpand(): void {
    this._expanded = !this._expanded;
    this.updateComplete.then(() => this._checkScrollOverflow());
  }

  private _isMobile(): boolean {
    return this._mobileQuery?.matches ?? window.matchMedia('(max-width: 640px)').matches;
  }

  private _lockBodyScroll(): void {
    if (this._isLocked) return;
    // Embedded mode (Phase 2) lives inline inside the host's layout — locking
    // the host's body scroll there would freeze the entire host page even
    // though the widget isn't presenting modally. Never lock for embedded.
    if (this.mode === 'embedded') return;
    // Flyout mode is non-modal: on desktop the host page stays scrollable
    // BEHIND the popover panel (intentional — users may be reading host
    // content while chatting). On mobile the flyout collapses to full-screen,
    // occluding the host, so the lock is the same as classic-on-mobile to
    // prevent scroll-bleed under the iOS keyboard. So: lock iff mobile,
    // regardless of mode (other than embedded).
    if (this._isMobile()) {
      lockBodyScroll();
      this._isLocked = true;
    }
  }

  private _unlockBodyScroll(): void {
    if (this._isLocked) {
      unlockBodyScroll();
      this._isLocked = false;
    }
  }

  private _onMobileChange = (): void => {
    if (!this._open) return;
    // Lock iff the new viewport is mobile (flyout and classic both go
    // full-screen there and need the lock to prevent host-body scroll
    // bleed under the iOS keyboard). Desktop unlocks regardless of mode —
    // flyout is non-modal, so the host page is meant to stay scrollable.
    if (this._isMobile()) {
      this._lockBodyScroll();
    } else {
      this._unlockBodyScroll();
    }
  };

  private _onDarkChange = (): void => {
    // Only `auto` follows the system: an explicit theme, which the theme
    // button sets, keeps its palette whatever the OS does. The listener is
    // bound only while the query is held, so the query is there to read.
    // updated() re-applies the palette and re-rasters the glass on the flip.
    if (this.theme !== 'auto') return;
    this._darkMode = this._darkQuery!.matches;
  };

  private _toggle(): void {
    this._open = !this._open;
    if (this._open) {
      this._cancelIntro();
      this.updateComplete.then(() => this._inputEl?.focusInput());
    }
    // Notify hosts so they can sync routing, analytics, or surrounding UI.
    this.dispatchEvent(
      new CustomEvent('steward-open-change', {
        detail: { open: this._open },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ── Public imperative API ──
  // Embedded mode (Phase 2) needs a way for the host to open/close the panel
  // without the FAB; non-embedded hosts can also drive open state from app
  // code (e.g. open the chat from a help-menu link).

  /** Open the chat panel. No-op if already open. */
  open(): void {
    if (!this._open) this._toggle();
  }

  /** Close the chat panel. No-op if already closed. */
  close(): void {
    if (this._open) this._toggle();
  }

  /** Toggle the chat panel. */
  toggle(): void {
    this._toggle();
  }

  /** Whether the panel is currently open. */
  get isOpen(): boolean {
    return this._open;
  }

  /** Programmatically submit a message as if the user typed it. Trims
   *  whitespace and dispatches the same `steward-submit` event a typed
   *  send would, so external listeners (e.g. analytics on the host page)
   *  fire identically to user-typed input. The dispatched event carries
   *  `detail.programmatic = true` so hosts that want to filter typed vs.
   *  programmatic submits can do so.
   *
   *  Auto-opens the panel if closed (matches the UX of "ask STEWARD
   *  about X" deep links and host-driven prompts). A conversation it starts
   *  opens with the AI disclosure line, because the submit takes the welcome
   *  card away at once.
   *
   *  Returns true if dispatched, false if no-op:
   *    - Empty / whitespace-only message after trim
   *    - A stream is already in progress (same guard as the typed path)
   *    - The input element could not be reached (panel mount failed)
   *
   *  Conversation continuity matches the typed path — multiple calls
   *  reuse `_conversationId` until the user clicks New, in the row below
   *  the composer (the speech-bubble-with-plus glyph). */
  async send(message: string): Promise<boolean> {
    if (typeof message !== 'string') return false;
    const trimmed = message.trim();
    if (!trimmed || this._isStreaming) return false;

    if (!this._open) {
      this.open();
      await this.updateComplete;
    }

    // Re-check after the await — a concurrent caller (or a host event
    // handler that fired during the open transition) may have raced us
    // into a streaming state. Without this, a second send() in the same
    // microtask would pass the top-level guard, await, then dispatch into
    // a busy widget whose own guard silently drops the call. Returning
    // false here gives the caller a signal.
    if (this._isStreaming) return false;
    const input = this._inputEl;
    if (!input) return false;

    if (this._messages.length === 0) this._disclosureAtHead = true;
    input.dispatchEvent(
      new CustomEvent('steward-submit', {
        detail: { message: trimmed, programmatic: true },
        bubbles: true,
        composed: true,
      }),
    );
    return true;
  }

  private _handleSubmit(e: CustomEvent<{ message: string }>): void {
    const { message } = e.detail;
    if (!message || this._isStreaming) return;

    this._error = null;

    // Add user message
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
    };
    this._messages = [...this._messages, userMsg];

    // Add streaming assistant placeholder (typing indicator shown via streaming + empty content)
    const assistantMsg: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      streaming: true,
      sources: [],
      thinkingSteps: [],
    };
    this._messages = [...this._messages, assistantMsg];
    this._isStreaming = true;
    this._cancelRequested = false;
    this._deliveryStarted = false;
    this._turnStopped = false;
    this._revealSource = '';
    this._revealCursor = 0;
    this._revealTotal = false;
    this._revealBlocks = [];
    // A new turn never inherits the previous turn's announcement.
    this._grewWhileAway = false;
    this._revealLastReleaseAt = Number.NEGATIVE_INFINITY;
    this._turnStartMs = typeof performance !== 'undefined' ? performance.now() : 0;
    this._userScrolledAway = false;

    this._scrollToBottom();
    this._streamResponse(message, assistantMsg.id);
  }

  private async _streamResponse(
    message: string,
    assistantMsgId: string,
  ): Promise<void> {
    const client = this._getClient();
    if (client === null) {
      // Unconfigured (no attribute URL, no baked default): nothing to
      // fetch from, so the turn finalizes locally with the same shape
      // a stream error would leave behind.
      this._flushReveal();
      this._updateMessage(assistantMsgId, (msg) => {
        msg.role = 'error';
        msg.content = 'This chat is not connected to an API.';
        msg.streaming = false;
      });
      this._isStreaming = false;
      this._scrollToBottom(true, true);
      return;
    }

    // Guards the cancelled→done sequence: a server-side cancel emits `cancelled`
    // then a trailing `done`. onCancelled finalizes the turn; this flag makes
    // the subsequent onDone idempotent so it can't re-finalize or fire the
    // completed-message analytics for a turn that was stopped.
    let finalized = false;
    // Set when the output guardrail replaced this turn's answer with the
    // platform's fallback. The turn still finalizes on `done`, but without
    // the trace id, so the fallback is never offered for rating.
    let replaced = false;

    const callbacks: StreamCallbacks = {
      // First event of the stream: the server-minted conversation id. Captured
      // immediately so a Stop (cancel) can target this turn — including turn 1,
      // before any message has streamed. Sole source of the id (the late
      // `metadata` event no longer carries it).
      onConversation: (data) => {
        this._conversationId = data.conversationId;
        this._saveSession();
      },

      // Precedes the answer's tokens. The server's word on whether the answer
      // is the model's is stored as given; nothing here re-derives it.
      onMetadata: (data) => {
        this._updateMessage(assistantMsgId, (msg) => {
          msg.aiGenerated = data.aiGenerated === true;
        });
      },

      onSource: (data) => {
        this._updateMessage(assistantMsgId, (msg) => {
          const existing = msg.sources || [];
          // Deduplicate by filename — show each source doc once
          if (!existing.some((s) => s.filename === data.filename)) {
            msg.sources = [...existing, data];
          }
        });
      },

      onToken: (data) => {
        // A local stop already froze this turn; ignore any tokens still buffered
        // in the transport.
        if (this._turnStopped) return;
        // Backstop for the single-round gap: a pre-token Stop fired the server
        // cancel, but the answer is streaming anyway — the turn had no second
        // node boundary for the server to re-poll the flag, so the server-side
        // cancel could not act. The arriving token is the proof it won't stop
        // this turn, so take over client-side: abandon it rather than pay out
        // tokens the user already asked to stop. Target this assistant message
        // so the local stop finalizes the right bubble.
        if (this._cancelRequested) {
          this._revealMsgId = assistantMsgId;
          this._stopTurnLocally();
          return;
        }
        // First token: the answer is now streaming, so a Stop becomes a local
        // freeze rather than a server cancel (see _handleStop).
        this._deliveryStarted = true;
        // Buffer tokens and drip them via requestAnimationFrame for typewriter effect
        this._revealMsgId = assistantMsgId;
        this._revealSource += data.content;
        this._startReveal();
        // Per-token DOM event for diagnostic tooling (the demo's raw SSE
        // console gates on this). Fires unconditionally — visibility is
        // listener-side, so the widget surface is the same whether anyone
        // listens or not.
        this.dispatchEvent(
          new CustomEvent('steward-token', {
            detail: { content: data.content, msgId: assistantMsgId },
            bubbles: true,
            composed: true,
          }),
        );
      },

      onArtifact: (data) => {
        const next = new Map(this._artifacts);
        next.set(data.artifactId, {
          imageUrl: data.imageUrl,
          downloadUrl: data.downloadUrl,
          description: data.description,
        });
        this._artifacts = next;
      },

      onThinking: (data) => {
        // Append the step to the assistant message's timeline. The
        // updater builds a fresh array reference so Lit's @property
        // reactivity on <steward-thinking>.steps fires the re-render.
        this._updateMessage(assistantMsgId, (msg) => {
          msg.thinkingSteps = [...(msg.thinkingSteps || []), data];
        });
        // Follow the growing timeline. The step renders in the child
        // <steward-thinking> one microtask after this host re-render, so defer
        // a frame before pinning or scrollHeight is read pre-layout. Respect a
        // user who scrolled up.
        if (!this._userScrolledAway) {
          this._scrollToBottom(false, true);
        }
      },

      onGuardrail: (data) => {
        if (!data.passed) {
          this._flushReveal();
          if (data.type === 'input') {
            const shown = data.reason || 'Your message could not be processed.';
            this._updateMessage(assistantMsgId, (msg) => {
              msg.content = shown;
              // Drop the released blocks with the answer they belonged to:
              // the element prefers `blocks` when present, so leaving them
              // would keep painting the text this rejection replaces.
              msg.blocks = undefined;
              msg.streaming = false;
            });
            this._isStreaming = false;
            // The input-reject path is followed by a trailing `done`; mark the
            // turn finalized so it can't re-finalize / fire completed analytics.
            finalized = true;
            // A host that drives this element through `send()` cannot see the
            // bubble above, and a refused message fires no `steward-message`,
            // so this is that host's one signal. It carries exactly what the
            // bubble carries: the same sentence, under a code of its own.
            this.dispatchEvent(
              new CustomEvent('steward-error', {
                detail: { code: 'INPUT_REFUSED', message: shown },
                bubbles: true,
                composed: true,
              }),
            );
          }
          // Output guardrail: the backend already replaced the response with a
          // safe fallback, and this event precedes it, so the tokens that follow
          // ARE the fallback. The turn stays open: they reveal like any answer
          // and the trailing `done` finalizes it, but unrateable.
          if (data.type === 'output') {
            replaced = true;
          }
        }
      },

      onCancelled: (data) => {
        // Server acknowledged a Stop. Freeze the typewriter, keep whatever
        // partial answer arrived, mark the turn cancelled, and re-enable input.
        // Sets `finalized` so the trailing `done` is a no-op.
        if (this._turnStopped || finalized) return;
        finalized = true;
        this._flushReveal();
        this._updateMessage(assistantMsgId, (msg) => {
          msg.streaming = false;
          msg.cancelled = true;
          // No `responding` fires on the cancel path, so this is the
          // collapsed-timeline aggregate source.
          msg.elapsedMs = data.elapsedMs;
        });
        this._isStreaming = false;
        this._saveSession();
        this.updateComplete.then(() => {
          if (this.shadowRoot?.contains(this.shadowRoot?.activeElement)) {
            this._inputEl?.focusInput();
          }
        });
      },

      onDone: (data) => {
        // Idempotent after a cancel: the cancel path already finalized the turn
        // and the trailing `done` must not re-finalize or fire completed-message
        // analytics for a stopped turn. `_turnStopped` covers a local mid-stream
        // stop, whose trailing `done` may still be buffered in the transport.
        if (this._turnStopped || finalized) return;
        // Let the typewriter drip loop drain remaining tokens naturally,
        // then finalize. If no typewriter is active, finalize immediately.
        const finalize = () => {
          finalized = true;
          this._updateMessage(assistantMsgId, (msg) => {
            msg.streaming = false;
            // Aggregate fallback; the `responding` step's elapsedMs is
            // preferred when present (it excludes delivery).
            msg.elapsedMs = data.elapsedMs;
            // The trace id makes this answer rateable: its presence is
            // what renders the thumbs affordance. Terminals without one
            // (tail-timeout done) simply never grow thumbs, and a replaced
            // answer drops it: that text is the platform's fallback, not a
            // model answer, so there is nothing of the model's to rate.
            msg.traceId = replaced ? undefined : data.traceId;
          });
          this._isStreaming = false;
          this._grewWhileAway = false;
          this._saveSession();

          // Re-pin to bottom after the streaming-end re-render. The
          // markdown parser switches modes when streaming flips (T-37:
          // streaming guard requires trailing \n on the last body row;
          // non-streaming makes it optional), so a table whose final
          // row lacked a trailing \n grows by one row at this exact
          // moment. unsafeHTML swaps the DOM and the layout shift can
          // strand the user above the new bottom. The IO callback no
          // longer fires (it gates on _isStreaming), so the auto-pin
          // wouldn't catch it. Pin explicitly here.
          if (!this._userScrolledAway) {
            this._scrollToBottom();
          }

          // Re-focus input only if widget still has focus
          this.updateComplete.then(() => {
            if (this.shadowRoot?.contains(this.shadowRoot?.activeElement)) {
              this._inputEl?.focusInput();
            }
          });

          // Analytics hook
          this.onMessage?.({
            type: 'message',
            conversationId: this._conversationId,
            usage: data.usage,
          });

          // Dispatch DOM event
          this.dispatchEvent(
            new CustomEvent('steward-message', {
              detail: {
                conversationId: this._conversationId,
                usage: data.usage,
              },
              bubbles: true,
              composed: true,
            }),
          );
        };

        // D5: `done` is a COMPLETENESS signal, not a stop. It makes
        // segmentation total — every remaining block's end becomes provable —
        // and the paced releases run out inside the bound. It used to flush.
        // It cannot: the backend buffers the whole answer and the wire is a
        // burst, so `done` lands milliseconds after the last token on every
        // turn but the first, and the flush collapsed the reveal into a single
        // paint, taking the pacing, the caret and the pill's new-content
        // semantics with it. The runaway it guarded against is what the bound
        // already prevents.
        this._revealTotal = true;
        if (this._revealMsgId !== null && !this._revealComplete()) {
          this._onRevealDone = finalize;
          // The loop parks itself whenever nothing is provable. `done` just
          // made the remainder provable, so wake it.
          this._startReveal();
        } else {
          this._flushReveal();
          finalize();
        }
      },

      onError: (data) => {
        // The server emits a terminal event THEN a trailing `done` on the
        // timeout (error+done) and guardrail-fail (guardrail+done) paths. Guard
        // against that trailing `done` re-finalizing this turn (which would fire
        // completed-message analytics for a failed turn), and against a stale
        // re-entry clobbering an already-finalized message.
        if (finalized) return;
        finalized = true;
        this._flushReveal();
        const shown = data.message || 'An error occurred. Please try again.';
        this._updateMessage(assistantMsgId, (msg) => {
          msg.role = 'error';
          msg.content = shown;
          // The element paints `blocks` ahead of `content`: drop the released
          // blocks, or the answer this sentence replaces stays on screen.
          msg.blocks = undefined;
          msg.streaming = false;
          // The sentence replaced the answer, and it is not the model's: an
          // error can follow a marked answer (a failed save after delivery).
          msg.aiGenerated = false;
        });
        this._isStreaming = false;
        // Always surface an error, even if the user had scrolled up mid-turn:
        // force the error bubble into view (deferFrame so the changed height
        // lays out first).
        this._scrollToBottom(true, true);

        // A host that drives this element through `send()` cannot see the
        // bubble above — its panel may not be on screen at all — and no
        // other event fires on a failed turn, so silence is the only thing
        // it could otherwise observe, and silence is also what a slow answer
        // looks like. This is that host's one signal, and it carries exactly
        // what the bubble carries: nothing leaves the element that a visitor
        // looking at it could not already read. The `finalized` guard above
        // is what makes it one event per turn, including on the server's
        // error-then-trailing-`done` paths.
        this.dispatchEvent(
          new CustomEvent('steward-error', {
            detail: { code: data.code, message: shown },
            bubbles: true,
            composed: true,
          }),
        );

        // Narrower and older than the event above, and kept: a refused
        // `apiKey` is a deployment fault no client-side retry can clear,
        // which is a different thing to tell a host than "this turn died".
        if (data.code === 'AUTH_ERROR') {
          this.dispatchEvent(
            new CustomEvent('steward-auth-error', {
              bubbles: true,
              composed: true,
            }),
          );
        }
      },
    };

    try {
      await client.chat(
        {
          message,
          conversationId: this._conversationId ?? undefined,
        },
        callbacks,
      );
    } catch (e) {
      console.warn('[steward-chat] Stream request failed:', e);
      this._flushReveal();
      this._updateMessage(assistantMsgId, (msg) => {
        if (msg.streaming) {
          msg.role = 'error';
          // Model tokens that already streamed stay, and keep their mark.
          // With none, the sentence below is the widget's own.
          if (!msg.content) {
            msg.content = 'An error occurred. Please try again.';
            msg.aiGenerated = false;
          }
          msg.streaming = false;
        }
      });
      this._isStreaming = false;
      this._scrollToBottom(true, true);
    }
  }

  // ── Reveal ──

  /** Paint everything that has arrived, at once.
   *
   *  Shared by reduced motion and by every terminal that REPLACES or ABANDONS
   *  the answer. Not a pacing decision: continuing to pay out an answer that
   *  has been superseded would be the bug. */
  private _paintRemainder(): void {
    if (!this._revealMsgId || this._revealPending === 0) return;
    this._revealCursor = this._revealSource.length;
    const settled = this._revealSource;
    const painted =
      this.revealMode === 'typewriter' ? null : segmentBlocks(settled, true).map((b) => b.text);
    if (painted) this._revealBlocks = painted;
    this._updateMessage(this._revealMsgId, (msg) => {
      msg.content = settled;
      if (painted) msg.blocks = painted;
    });
    // The loop follows the bottom each frame; a one-shot paint lands outside
    // it, so follow a user who was tracking the stream.
    if (!this._userScrolledAway) {
      this._scrollToBottom();
    }
  }

  /** Is the answer fully on screen? Only true once `done` has made
   *  segmentation total AND the pacing has run out. */
  private _revealComplete(): boolean {
    if (!this._revealTotal) return false;
    if (this.revealMode === 'typewriter') return this._revealPending === 0;
    return this._revealBlocks.length >= segmentBlocks(this._revealSource, true).length;
  }

  /** Release at most one block, if the pace allows one this frame. */
  private _releaseBlock(): RevealStep {
    const blocks = segmentBlocks(this._revealSource, this._revealTotal);
    const released = this._revealBlocks.length;
    if (released >= blocks.length) {
      return this._revealTotal ? 'done' : 'idle';
    }

    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    const interval = Math.min(REVEAL_STEP_MS, REVEAL_BOUND_MS / Math.max(1, blocks.length));
    if (now - this._revealLastReleaseAt < interval) return 'pacing';

    const next = blocks[released];
    this._revealBlocks.push(next.text);
    this._revealCursor = next.end;
    this._revealLastReleaseAt = now;

    if (this._revealMsgId) {
      const settled = this._revealSource.slice(0, this._revealCursor);
      // A fresh array so Lit sees the property change. Existing indices hold
      // the SAME strings, so their unsafeHTML parts do not re-render and the
      // container for block N survives block N+1 — which is what makes the
      // fade run once per block and settled text structurally stable.
      const painted = [...this._revealBlocks];
      this._updateMessage(this._revealMsgId, (msg) => {
        msg.content = settled;
        msg.blocks = painted;
      });
      if (this._userScrolledAway) {
        this._grewWhileAway = true;
      } else {
        this._scrollToBottom();
      }
    }
    return this._revealComplete() ? 'done' : 'released';
  }

  /** Advance the cursor by one frame of characters (reveal-mode="typewriter").
   *  Never sets `blocks`, so the message renders from `content` as one
   *  container exactly as it always did. */
  private _dripCharacters(): RevealStep {
    if (this._revealPending === 0) return this._revealTotal ? 'done' : 'idle';
    const chars = this._revealSource.slice(
      this._revealCursor,
      this._revealCursor + this._typewriterCharsPerFrame,
    );
    this._revealCursor += chars.length;
    if (this._revealMsgId && chars) {
      this._updateMessage(this._revealMsgId, (msg) => {
        msg.content += chars;
      });
      if (!this._userScrolledAway) {
        this._scrollToBottom();
      }
    }
    return this._revealComplete() ? 'done' : 'released';
  }

  private _startReveal(): void {
    if (this._revealActive) return;

    // Reduced motion gets the answer in one paint, in EITHER mode. Not a third
    // mode — the absence of one. The reveal is JS-driven motion that the CSS
    // @media gate cannot reach.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      this._paintRemainder();
      if (this._onRevealDone) {
        const cb = this._onRevealDone;
        this._onRevealDone = null;
        cb();
      }
      return;
    }

    this._revealActive = true;

    const tick = () => {
      const step =
        this.revealMode === 'typewriter' ? this._dripCharacters() : this._releaseBlock();

      if (step === 'released' || step === 'pacing') {
        this._revealRafId = requestAnimationFrame(tick);
        return;
      }

      // 'idle': nothing provable yet — park, and let the next token restart.
      // 'done': the answer is fully revealed, so a deferred finalize may run.
      this._revealActive = false;
      this._revealRafId = null;
      if (step === 'done' && this._onRevealDone) {
        const cb = this._onRevealDone;
        this._onRevealDone = null;
        cb();
      }
    };

    this._revealRafId = requestAnimationFrame(tick);
  }

  private _flushReveal(): void {
    // Cancel any pending frame to prevent zombie releases.
    if (this._revealRafId != null) {
      cancelAnimationFrame(this._revealRafId);
      this._revealRafId = null;
    }
    this._paintRemainder();
    this._revealActive = false;
    this._revealMsgId = null;
    this._onRevealDone = null;
  }

  // ── Helpers ──

  private _updateMessage(
    id: string,
    updater: (msg: Message) => void,
  ): void {
    this._messages = this._messages.map((msg) => {
      if (msg.id === id) {
        const updated = { ...msg };
        updater(updated);
        return updated;
      }
      return msg;
    });
  }

  private _checkScrollOverflow(): void {
    const c = this._messagesContainer;
    if (!c) return;
    const shouldShow = c.scrollHeight - c.scrollTop - c.clientHeight > 100;
    if (this._showScrollBtn !== shouldShow) {
      this._showScrollBtn = shouldShow;
    }
  }

  // Scroll handler. During streaming the IntersectionObserver on the bottom
  // sentinel owns `_userScrolledAway` — we deliberately do NOT compute
  // at-bottom from scroll position here, because the browser dispatches
  // scroll events on a later tick than the synchronous scrollTo, racing
  // typewriter drips when layout shifts (e.g. Markdown table snap-in)
  // change `scrollHeight` between drip and event-dispatch. Outside of
  // streaming, this only refreshes the scroll-to-bottom button state.
  private _onMessagesScroll(): void {
    if (this._isStreaming) return;
    this._checkScrollOverflow();
  }

  // Synchronous user-scroll-intent handler. Bound on `.messages-container`
  // for wheel / touchmove / keydown — these fire in the same task as the
  // input event, so we can flip `_userScrolledAway` BEFORE the next
  // typewriter drip's auto-pin reads it.
  //
  // Asymmetric thresholds: leaving the bottom uses a strict 40px band
  // (matches the IO rootMargin — a tiny gesture at the edge shouldn't
  // count as scrolling away). REJOINING the bottom uses a generous
  // half-clientHeight band on wheel-down only. The reason: under heavy
  // streaming the bottom moves away faster than a user can wheel toward
  // it, so a strict re-attach threshold is unreachable. When the user
  // wheels down within the bottom half, we treat it as "I want to follow
  // the stream again" — reset the flag and explicitly snap to bottom so
  // auto-pin re-engages cleanly without waiting for the next drip.
  private _onUserScrollIntent = (e: Event): void => {
    if (!this._isStreaming) return;
    const c = this._messagesContainer;
    if (!c) return;
    const room = c.scrollHeight - c.scrollTop - c.clientHeight;

    if (
      e.type === 'wheel' &&
      (e as WheelEvent).deltaY > 0 &&
      this._userScrolledAway &&
      room <= c.clientHeight * 0.5
    ) {
      this._userScrolledAway = false;
      this._showScrollBtn = false;
      this._scrollToBottom();
      return;
    }

    if (room <= 40) return;
    if (!this._userScrolledAway) {
      this._userScrolledAway = true;
      this._showScrollBtn = true;
    }
  };

  private _scrollToBottom(smooth = false, deferFrame = false): void {
    this.updateComplete.then(() => {
      const run = () => {
        const container = this._messagesContainer;
        if (!container) return;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: smooth ? 'smooth' : 'instant',
        });
        if (this._showScrollBtn) this._showScrollBtn = false;
        if (this._isStreaming) this._userScrolledAway = false;
      };
      if (deferFrame) requestAnimationFrame(run);
      else run();
    });
  }

  // ── Session persistence (sessionStorage) ──

  private get _sessionKey(): string {
    return 'steward_session';
  }

  /** Separate key for the thinking-display UI preference. It is a per-session
   *  preference distinct from the conversation payload, so clearing the
   *  conversation (which removes `_sessionKey`) must NOT reset the mode. The
   *  `:thinking-mode` suffix shares the `_sessionKey` base so the two are scoped
   *  identically. */
  private get _thinkingModeKey(): string {
    return `${this._sessionKey}:thinking-mode`;
  }

  /** Persist the chosen thinking-display mode. Storage access can throw on
   *  embedding pages / private-mode browsers; never let that break rendering. */
  private _persistThinkingMode(): void {
    try {
      sessionStorage.setItem(this._thinkingModeKey, this._thinkingMode);
    } catch (e) {
      console.warn('[steward-chat] Failed to save thinking mode to sessionStorage:', e);
    }
  }

  /** Restore the thinking-display mode from a prior turn in this session.
   *  Falls back to the default ('full', already set on `_thinkingMode`) on any
   *  error, absence, or tampered value. */
  private _restoreThinkingMode(): void {
    try {
      const stored = sessionStorage.getItem(this._thinkingModeKey);
      if (stored === 'full' || stored === 'step' || stored === 'hidden') {
        this._thinkingMode = stored;
      }
    } catch (e) {
      console.warn('[steward-chat] Failed to restore thinking mode (unavailable):', e);
    }
  }

  private _saveSession(): void {
    try {
      const artifacts: Record<string, { imageUrl: string; downloadUrl: string; description: string }> = {};
      this._artifacts.forEach((v, k) => { artifacts[k] = v; });
      const data = {
        conversationId: this._conversationId,
        messages: this._messages
          .filter((m) => !m.streaming)
          .map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            sources: m.sources,
            thinkingSteps: m.thinkingSteps,
            // Persist the stopped marker + turn total so a reloaded session
            // still shows the "Stopped." caption and the collapsed-timeline
            // duration, not just the (bare) timeline.
            cancelled: m.cancelled,
            elapsedMs: m.elapsedMs,
            // Persist the trace id + chosen thumb so a restored answer keeps
            // a live feedback affordance with its selection intact.
            traceId: m.traceId,
            feedback: m.feedback,
            // A restored answer keeps its machine-readable mark.
            aiGenerated: m.aiGenerated,
          })),
        artifacts,
      };
      sessionStorage.setItem(this._sessionKey, JSON.stringify(data));
    } catch (e) {
      console.warn('[steward-chat] Failed to save session to sessionStorage:', e);
    }
  }

  private _restoreSession(): void {
    try {
      const raw = sessionStorage.getItem(this._sessionKey);
      if (!raw) return;
      const data = JSON.parse(raw);

      // Validate session shape before restoring
      if (data.conversationId != null && typeof data.conversationId !== 'string') return;
      if (!Array.isArray(data.messages)) return;

      const validRoles = new Set(['user', 'assistant', 'error']);
      const validMessages = data.messages.filter(
        (m: Record<string, unknown>) =>
          m &&
          typeof m === 'object' &&
          typeof m.id === 'string' &&
          typeof m.role === 'string' &&
          validRoles.has(m.role as string) &&
          typeof m.content === 'string',
      );

      this._conversationId = data.conversationId ?? null;
      this._messages = validMessages as Message[];

      // Restore artifacts Map (persisted as plain object)
      if (data.artifacts && typeof data.artifacts === 'object') {
        const restoredArtifacts = new Map<string, { imageUrl: string; downloadUrl: string; description: string }>();
        for (const [k, v] of Object.entries(data.artifacts)) {
          const art = v as Record<string, unknown>;
          if (typeof art.imageUrl === 'string') {
            restoredArtifacts.set(k, {
              imageUrl: art.imageUrl as string,
              downloadUrl: (art.downloadUrl as string) || '',
              description: (art.description as string) || '',
            });
          }
        }
        this._artifacts = restoredArtifacts;
      }
    } catch (e) {
      console.warn('[steward-chat] Failed to restore session (corrupted or unavailable):', e);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'steward-chat': StewardChat;
  }
}
